import React, { useState, useEffect, useMemo, useRef, useId } from "react";
import {
  getSettings,
  updateSettings,
  appendTrade,
  appendLesson,
  fetchJournalRows,
  SHEET_HEADERS,
  getNextTradeNumber,
  getUserSettings,
  updateUserSettings
} from "../services/FirestoreService";
import Confetti from 'react-confetti';
import { useWindowSize } from 'react-use';
import { FaRegClock, FaCalculator, FaChartLine, FaExclamationTriangle } from "react-icons/fa";
import PreTradeChecklistModal from './PreTradeChecklistModal';
import { savePreTradeChecklist, getTodayPreTradeChecklist,setChecklistShown } from "../services/FirestoreService";
import { createPortal } from "react-dom";
import SimpleCalculator from './SimpleCalculator'; 
import { FOREX_PAIRS, FOREX_INDICES, isIndexSymbol } from "../constants/instruments";
import { doc, onSnapshot, collection, query, where } from "firebase/firestore";
import { db } from "../firebase"

// --- CONSTANTS ---

const ASSET_CLASSES = ["Futures", "Forex", "Stocks"];

const FUTURES_OPTIONS = [
  ["YM - Dow - $5.00", "YM - US30 - $5.00"],
  ["MYM - Dow Micro - $0.50", "MYM - US30 Micro - $0.50"],
  ["ES - SXP 500 - $12.50", "ES - SXP 500 - $12.50"],
  ["MES - SXP 500 Micro - $1.25", "MES - SXP 500 Micro - $1.25"],
  ["NQ - NAS - $5.00", "NQ - NAS - $5.00"],
  ["MNQ - NAS Micro - $0.50", "MNQ - NAS Micro - $0.50"],
  ["GC - Gold - $10.00", "GC - Gold - $10.00"],
  ["MGC - Gold Micro - $1.00", "MGC - Gold Micro - $1.00"],
  ["CL - Oil - $10.00", "CL - Oil - $10.00"],
  ["MCL - Oil Micro - $1.00", "MCL - Oil Micro - $1.00"],
  ["RTY - Russell - $5.00", "RTY - Russell - $5.00"],
  ["M2K - Russell Micro - $0.50", "M2K - Russell Micro - $0.50"],
];

const TIMEFRAME_OPTIONS = ["30s","1m","2m","3m","5m","10m","15m","30m","1h","2h","4h","D","W","M"];
const SESSION_OPTIONS = ["New York", "London", "Asian", "Mixed"];
const RULES_OPTIONS = ["Yes", "No"];
const GRADE_OPTIONS = ["A", "B", "C", "D", "E", "F"];
const POSITION_OPTIONS = ["Buy", "Sell"];
const MOOD_OPTIONS = ["1", "2", "3", "4", "5"];

// --- UI HELPERS ---

function SummaryRow({ label, value, red }) {
  return (
    <div className="flex justify-between items-center py-1">
      <span className="font-medium text-gray-700 dark:text-gray-300">{label}</span>
      <span className={red ? "text-red-600 font-bold" : "text-gray-900 dark:text-gray-100"}>
        {value}
      </span>
    </div>
  );
}

const IconGoal = () => (
  <span className="bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-300 rounded-full p-2 mr-2 shadow-sm">
    <svg width="24" height="24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="#16a34a" strokeWidth="2"/>
      <path d="M8 12l2 2l4-4" stroke="#16a34a" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  </span>
);

function Tooltip({ children, content, variant = "neutral", backdrop = false, closeOnEsc = true, lockScroll = true }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  
  const palette = {
    neutral: { panel: "bg-gray-900/95 text-white ring-white/10", arrow: "bg-gray-900/95", focus: "focus:ring-yellow-500" },
    yellow: { panel: "bg-yellow-600/95 text-white ring-yellow-300/30", arrow: "bg-yellow-600/95", focus: "focus:ring-yellow-500" },
    red: { panel: "bg-red-800/95 text-white ring-red-300/30", arrow: "bg-red-800/95", focus: "focus:ring-red-500" },
    blue: { panel: "bg-slate-900/95 text-white ring-blue-300/30", arrow: "bg-slate-900/95", focus: "focus:ring-blue-500" },
  }[variant] || { panel: "bg-gray-900/95 text-white ring-white/10", arrow: "bg-gray-900/95", focus: "focus:ring-yellow-500" };

  useEffect(() => {
    if (open && backdrop && closeOnEsc) {
      const handleEsc = (e) => e.key === "Escape" && setOpen(false);
      window.addEventListener("keydown", handleEsc);
      return () => window.removeEventListener("keydown", handleEsc);
    }
  }, [open, backdrop, closeOnEsc]);

  return (
    <>
      <span
        ref={triggerRef}
        aria-describedby={id}
        onMouseEnter={() => !backdrop && setOpen(true)}
        onMouseLeave={() => !backdrop && setOpen(false)}
        onClick={() => setOpen(!open)}
        className={`relative inline-flex items-center underline decoration-dotted underline-offset-2 cursor-help focus:outline-none ${palette.focus} rounded-[3px]`}
      >
        {children}
      </span>
      {open && createPortal(
        <div className="fixed inset-0 z-[9999] pointer-events-none flex items-center justify-center">
          {backdrop && <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px] pointer-events-auto" onClick={() => setOpen(false)} />}
          <div
            id={id}
            role="tooltip"
            style={{ 
              position: 'absolute', 
              top: triggerRef.current ? triggerRef.current.getBoundingClientRect().top - 10 : 0,
              left: triggerRef.current ? triggerRef.current.getBoundingClientRect().left : 0,
              transform: 'translate(-50%, -100%)'
            }}
            className={`pointer-events-auto select-text max-w-[min(90vw,28rem)] w-max break-words rounded-lg px-4 py-3 text-[13px] leading-relaxed font-normal shadow-2xl ring-1 backdrop-blur-sm transition-opacity duration-200 opacity-100 scale-100 ${palette.panel}`}
          >
            {content}
            <span className={`absolute top-full left-1/2 -translate-x-1/2 h-3 w-3 rotate-45 ${palette.arrow} shadow -mt-1.5`} />
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

// --- UTILITIES ---

function formatCurrency(val, currency = "USD") {
  if (val === "" || val === null || isNaN(Number(val))) return "";
  return Number(val).toLocaleString("en-US", { style: "currency", currency: currency, minimumFractionDigits: 2 });
}

const FOREX_VALUES = new Set(FOREX_PAIRS.map(([v]) => v));
const FUTURES_VALUES = new Set(FUTURES_OPTIONS.map(([full]) => full.split(" - ")[0]));

// 2. Product Normalizer
function normalizeProductValue(raw) {
  if (!raw) return "";
  const firstPart = String(raw).split(" - ")[0].trim().toUpperCase();
  return firstPart; // Simple normalization for now
}

function parseCurrency(str) {
  const n = Number(String(str ?? "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function getTodayLocalISO() {
  const today = new Date();
  const tzOffset = today.getTimezoneOffset() * 60000;
  return new Date(today - tzOffset).toISOString().slice(0, 10);
}

function to24HourTime(str) {
  if (!str) return "";
  if (/^\d{2}:\d{2}$/.test(str)) return str;
  const m = str.match(/^(\d{1,2}):(\d{2})\s*([APap][Mm])$/);
  if (m) {
    let [, h, mins, ap] = m;
    let hour = parseInt(h, 10);
    if (ap.toUpperCase() === "PM" && hour !== 12) hour += 12;
    if (ap.toUpperCase() === "AM" && hour === 12) hour = 0;
    return `${hour.toString().padStart(2, "0")}:${mins}`;
  }
  return "";
}

function timeStringToSeconds(str) {
  if (!str) return 0;
  const parts = str.split(":").map(Number);
  if (parts.length >= 2) {
    return parts[0] * 3600 + parts[1] * 60 + (parts[2] || 0);
  }
  return 0;
}

function secondsToHHMMSS(totalSeconds) {
  totalSeconds = Math.max(0, totalSeconds);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return [
    h.toString().padStart(2, "0"),
    m.toString().padStart(2, "0"),
    s.toString().padStart(2, "0"),
  ].join(":");
}

function getDurationHHMMSS(start, end) {
  if (!start || !end) return "";
  let s = timeStringToSeconds(to24HourTime(start));
  let e = timeStringToSeconds(to24HourTime(end));
  let diff = e - s;
  if (diff < 0) diff += 24 * 3600; // Crossed midnight
  return secondsToHHMMSS(diff);
}

function getPipSizeForSymbol(symbol) {
  if (!symbol) return 0.0001;
  const s = String(symbol).toUpperCase();
  if (s.includes("JPY")) return 0.01;
  return 0.0001;
}

function computePipsFromPricesAbs(product, entry, exit) {
  const e = Number(entry);
  const x = Number(exit);
  if (!Number.isFinite(e) || !Number.isFinite(x)) return null;
  
  const diff = Math.abs(x - e);
  const pipSize = getPipSizeForSymbol(product);
  return pipSize > 0 ? Math.round(diff / pipSize) : 0;
}

// 5. Risk Budget Helper
function getRiskBudgetDollar(settings, balance) {
  const mode = settings["Risk Mode"] || "%";
  const riskVal = Number(mode === "$" ? settings["Risk Dollar Amount"] : settings["Risk Percentage"]);
  const budget = mode === "$" ? riskVal : (balance * (riskVal / 100));
  return { budget };
}

function toAmPm(str) {
  if (!str) return "";
  if (!/^\d{2}:\d{2}$/.test(str)) return str;
  let [h, m] = str.split(":");
  h = parseInt(h, 10);
  const ampm = h >= 12 ? "PM" : "AM";
  let hour12 = h % 12;
  if (hour12 === 0) hour12 = 12;
  return `${hour12.toString().padStart(2, "0")}:${m} ${ampm}`;
}

// --- LOGIC HELPERS ---

function getMultiplier(assetClass, product, settings = {}) {
  // 1. FUTURES: Extract dollar value from the product string (e.g., "$12.50")
  if (assetClass === "Futures") {
    const match = String(product).match(/\$\s?([0-9,.]+)/);
    return match ? parseFloat(match[1].replace(/,/g, "")) : 0;
  }

  // 2. FOREX: Handle different pip values for pairs vs indices
  if (assetClass === "Forex") {
    // Priority: User override in settings
    if (settings["Pip Value"]) return Number(settings["Pip Value"]);
    
    const sym = (product || "").toUpperCase();
    if (sym.startsWith("XAU")) return 1;    // Gold
    if (sym.startsWith("XAG")) return 5;    // Silver
    if (sym.includes("JPY")) return 9.09;   // JPY Pairs
    
    // Check if it's an index (US30, NAS100, SPX500)
    const FOREX_INDICES = ["US30", "NAS100", "SPX500", "GER30", "UK100"];
    if (FOREX_INDICES.some(idx => sym.includes(idx))) return 1;
    
    return 10; // Default Standard Lot pip value
  }

  // 3. STOCKS: Usually a 1:1 ratio per share
  return 1;
}

function getUnitLabel(assetClass) {
  if (assetClass === "Futures") return "Contracts";
  if (assetClass === "Forex") return "Lots";
  return "Shares";
}

function getCompoundingProjection({ assetClass, balance, goal, riskPercent, riskMode, riskDollar, stopLoss, takeProfit, maxContracts, minContracts, multiplier, entryPrice }) {
  let trades = [];
  let currentBalance = balance;
  let tradeCount = 0;
  
  while (currentBalance < goal && tradeCount < 10000) {
    const riskBudget = riskMode === "$" ? riskDollar : (currentBalance * riskPercent / 100);
    let size = 0;
    
    if (assetClass === "Stocks") {
      // For Stocks, riskBudget is the "Investment Amount", so Size = Investment / EntryPrice
      // [!code ++] FIX: Limit to 4 decimal places inside the loop
      if (entryPrice > 0) size = Math.floor((riskBudget / entryPrice) * 10000) / 10000;
    } else {
      // For Futures/Forex, riskBudget is the "Risk Amount", so Size = Risk / StopLoss
      if (stopLoss > 0 && multiplier > 0) size = riskBudget / (stopLoss * multiplier);
    }

    size = Math.min(size, maxContracts);
    
    if (assetClass === "Forex") size = Math.floor(size * 100) / 100;
    else if (assetClass !== "Stocks") size = Math.floor(size); 

    // FALLBACK TO MIN
    const usedSize = Math.max(size, minContracts);

    // Recalculate specs based on used size
    const tradeProfit = usedSize * (assetClass === "Stocks" ? takeProfit : (takeProfit * multiplier));
    const tradeRisk = usedSize * (assetClass === "Stocks" ? stopLoss : (stopLoss * multiplier));

    let isUnderfunded = false;
    if (assetClass === "Stocks" && entryPrice > 0) {
        if ((usedSize * entryPrice) > currentBalance) isUnderfunded = true;
    } else {
        if (size < minContracts) isUnderfunded = true;
    }

    trades.push({
      trade: tradeCount + 1,
      balance: currentBalance,
      riskDollar: riskBudget,
      size: usedSize,
      totalRisk: tradeRisk,
      tradeProfit: tradeProfit,
      nextBalance: currentBalance + tradeProfit,
      underfunded: isUnderfunded
    });

    currentBalance += tradeProfit;
    tradeCount++;
    if (currentBalance >= goal) break;
  }
  return { trades, tradeCount: trades.length };
}

function formatThreeLossMessage(line) {
  if (!line) return null;
  const highlights = {
    "Take a break!": "font-bold text-red-500 dark:text-red-500",
    "Trading is 80% Trading Psychology.": "font-bold text-blue-500 dark:text-blue-400",
    "Continue being Awesome!": "font-bold text-green-600 dark:text-green-400", 
    "BIG": "font-bold text-green-600 dark:text-green-400" 
  };
  const escaped = Object.keys(highlights).map(s => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|");
  const regex = new RegExp(`(${escaped})`, "g");
    return line.split(regex).map((part, i) =>
      highlights[part]
        ? <span key={i} className={highlights[part]}>{part}</span>
        : <React.Fragment key={i}>{renderWithTiltTooltip(part)}</React.Fragment>
    );
  }

  const TWO_LOSS_WARNING_TEXT = `You've had 2 losses in a row.

Evaluate: Take a break and review your recent trades. What can you learn from them before placing your next order?

Accept: Remember that losses are a part of trading. Learning to be okay with them is crucial for success.

Refocus: No strategy is 100%, but following your rules consistently is the only way to improve your chances.

You've got this. Apply what you learned in the next one!`;

function highlightHundredPercent(line) {
  if (!line) return null;
  return line.split(/(100%)/g).map((part, i) =>
    part === "100%" ? (
      <span key={i} className="font-extrabold text-yellow-500 dark:text-yellow-400">100%</span>
    ) : (
      <React.Fragment key={i}>{renderWithTiltTooltip(part)}</React.Fragment>
    )
  );
}

function renderWithTiltTooltip(text) {
  const parts = text.split(/(\btilt\b)/gi);
  return parts.map((part, idx) => {
    if (part.toLowerCase() === "tilt") {
      return (
        <Tooltip
          key={idx}
          variant="yellow"
          content={
            <div className="space-y-1">
              <div className="text-sm uppercase tracking-wide font-bold opacity-100">On Tilt</div>
              <p className="mt-5 text-[14px] leading-relaxed">
                A heightened emotional state (like frustration or euphoria) that leads to impulsive, undisciplined trading, ignoring your plan.
              </p>
              <p className="mt-5 text-[14px] leading-relaxed">
                Your brain has entered "fight or flight" mode. Your logical decision-making is offline, and you are no longer in control.
              </p>
              <p className="mt-6 text-[14px] font-bold leading-relaxed">What to do:</p>
              <ul className="mt-2 list-disc pl-4 text-[14px] opacity-100">
                <li>STOP TRADING IMMEDIATELY. This is the most important step.</li>
                <li>Step away from the screen. Reset your mind with a walk, exercise (particularly weight lifting), or another non-trading activity.</li>
                <li>Do not resume trading until you are calm and focused.</li>
              </ul>
            </div>
          }
        >
          tilt
        </Tooltip>
      );
    }
    return <React.Fragment key={idx}>{part}</React.Fragment>;
  });
}

function getLossProjection({ assetClass, balance, riskPercent, riskMode, riskDollar, stopLoss, maxContracts, minContracts, multiplier, entryPrice }) {
  let trades = [];
  let currentBalance = Number(balance);
  let tradeCount = 0;

  // Floor to $1.00 for Stocks
  const limit = assetClass === "Stocks" ? 1 : 0;

  // Use a strictly greater than check with a small epsilon for floating point safety
  while (currentBalance > (limit + 0.001) && tradeCount < 10000) {
    const riskBudget = riskMode === "$" ? riskDollar : (currentBalance * riskPercent / 100);
    let size = 0;

    if (assetClass === "Stocks") {
      if (entryPrice > 0) {
        size = Math.floor((riskBudget / entryPrice) * 10000) / 10000;
      }
    } else {
      if (stopLoss > 0 && multiplier > 0) size = riskBudget / (stopLoss * multiplier);
    }

    size = Math.min(size, maxContracts);
    
    if (assetClass === "Forex") size = Math.floor(size * 100) / 100;
    else if (assetClass !== "Stocks") size = Math.floor(size);

    let usedSize = assetClass === "Stocks" ? size : Math.max(size, minContracts);
    
    // Calculate raw loss and round it to 2 decimals for currency precision
    let lossAmount = usedSize * (assetClass === "Stocks" ? stopLoss : (stopLoss * multiplier));
    lossAmount = Math.round(lossAmount * 100) / 100;

    // Safety check: if the loss is essentially zero, stop to avoid infinite loop
    if (lossAmount <= 0) break;

    const nextBalance = Math.round((currentBalance - lossAmount) * 100) / 100;

    // If this trade hits or goes below $1.00, it's the final trade.
    if (nextBalance <= limit) {
      trades.push({
        trade: tradeCount + 1,
        balanceBefore: currentBalance,
        size: assetClass === "Forex" ? usedSize.toFixed(2) : usedSize,
        loss: Math.round((currentBalance - limit) * 100) / 100, // Final loss to reach exactly $1.00
        balanceAfter: limit
      });
      break; // Stop immediately; prevents trade 1394
    }

    trades.push({
      trade: tradeCount + 1,
      balanceBefore: currentBalance,
      size: assetClass === "Forex" ? usedSize.toFixed(2) : usedSize,
      loss: lossAmount,
      balanceAfter: nextBalance
    });

    currentBalance = nextBalance;
    tradeCount++;
  }
  return { trades, tradeCount: trades.length };
}

// --- MAIN COMPONENT ---

// Add isAdmin = false to the props
export default function CalculateForm({ userId, accountId, isAdmin = false }) {
  const [assetClass, setAssetClass] = useState("Futures");
  const [settings, setSettings] = useState({ 
    "Risk Mode": "%", 
    "Stop Loss Mode": "$", 
    "Take Profit Mode": "$",
    "Pretrade Checklist": "Yes",
    "Calculator Only": false,
    "Minimum Contracts": 1 
  });
  const [journal, setJournal] = useState({ 
    Date: getTodayLocalISO(), 
    Timeframe: "",
    "Actual Balance": "",
    "Entry Strategy": "",
    "Exit Strategy": "",
    "Session": "",
    "Win": false,
    "Loss": false
  });
  const [calculatedSummary, setCalculatedSummary] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingTrade, setSavingTrade] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  const [missingFields, setMissingFields] = useState([]);
  const [popupQueue, setPopupQueue] = useState([]);
  const [currentPopup, setCurrentPopup] = useState(null);
  const [showChecklist, setShowChecklist] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showZeroContractsPopup, setShowZeroContractsPopup] = useState(false);
  const [lastTotalRisk, setLastTotalRisk] = useState(0);
  const [lastActualRiskPercent, setLastActualRiskPercent] = useState(0);
  const [userPrefs, setUserPrefs] = useState({});
  const isPrivacyMode = userPrefs?.["Privacy Mode"] === true;
  const [userFavorites, setUserFavorites] = useState({ futures: [], forex: [] });
  const [pendingWarning, setPendingWarning] = useState(null); 

  const [projectionOpen, setProjectionOpen] = useState(false);
  const [lossProjectionOpen, setLossProjectionOpen] = useState(false);
  const [winProjection, setWinProjection] = useState({ tradeCount: 0, trades: [] });
  const [lossProjection, setLossProjection] = useState({ tradeCount: 0, trades: [] });

  const popupsAllowedRef = useRef(false);
  const entryTimeRef = useRef();
  const exitTimeRef = useRef();
  const actualBalRef = useRef(null);
  const [dailyGoalProg, setDailyGoalProg] = useState({ done: 0, target: 0, pct: 0 });
  const [dailyLossProg, setDailyLossProg] = useState({ lost: 0, limit: 0, pct: 0 });
  const [entryStrategies, setEntryStrategies] = useState([]);
  const [exitStrategies, setExitStrategies] = useState([]);
  const DEFAULT_TIME_ZONE = undefined;
  
  const { width, height } = useWindowSize();

  const handleChecklistConfirm = async (selections) => {
    try {
      if (selections && typeof selections === "object") {
        await savePreTradeChecklist(userId, accountId, selections); 
      }
      const today = getTodayLocalISO();
      await updateUserSettings(userId, { PretradeChecklistShownDate: today });
      setUserPrefs((p) => ({ ...p, PretradeChecklistShownDate: today }));
    } catch (e) {
      console.error("[PTC] confirm error", e);
      setUserPrefs((p) => ({ ...p, PretradeChecklistShownDate: getTodayLocalISO() }));
    } finally {
      setShowChecklist(false);
    }
  };

  const [subStatus, setSubStatus] = useState("");

    useEffect(() => {
      if (!userId) return;
      const subscribersRef = collection(db, "subscribers");
      const q = query(subscribersRef, where("userId", "==", userId));

      const unsubscribe = onSnapshot(q, (snapshot) => {
        if (!snapshot.empty) {
          setSubStatus(snapshot.docs[0].data().status || "");
        } else {
          setSubStatus("");
        }
      });
      return () => unsubscribe();
    }, [userId]);

    const isTrialing = subStatus === "TRIALING";

  useEffect(() => {
    if (!userId) return;
    const ref = doc(db, "users", userId, "settings", "userSettings"); 
    const unsub = onSnapshot(ref, snap => {
      const favs = snap.exists() ? (snap.data().favorites || { futures: [], forex: [] }) 
                                 : { futures: [], forex: [] };
      setUserFavorites(favs);
    });
    return unsub;
  }, [userId]);

  const { favOpts, nonFavOpts } = useMemo(() => {
    const isFutures = assetClass === "Futures";
    const allOpts = isFutures ? FUTURES_OPTIONS : FOREX_PAIRS; 
    const favList = isFutures ? (userFavorites?.futures || []) : (userFavorites?.forex || []);
    
    const favSet = new Set(favList.map(v => {
      return isFutures ? v.split(" - ")[0] : String(Array.isArray(v) ? v[0] : v).toUpperCase(); 
    }));

    const fav = [];
    const non = [];

    for (const opt of allOpts) {
      const val = Array.isArray(opt) ? opt[0] : opt;
      const label = Array.isArray(opt) ? opt[1] : opt;
      const checkVal = isFutures ? val.split(" - ")[0] : String(val).toUpperCase();
      
      if (favSet.has(checkVal)) {
        fav.push([val, label || val]);
      } else {
        non.push([val, label || val]);
      }
    }
    return { favOpts: fav, nonFavOpts: non };
  }, [assetClass, userFavorites]);

  // --- AUTO-CALCULATE ON LOAD ---
  useEffect(() => {
    const hasProduct = !!settings["Product"];
    const hasBalance = !!(settings["Running Balance"] || settings["Initial Balance"]);
    const hasRisk = Number(settings["Risk Mode"] === "$" ? settings["Risk Dollar Amount"] : settings["Risk Percentage"]) > 0;
    const hasStopLoss = Number(settings["Stop Loss"]) > 0;
    const hasTakeProfit = Number(settings["Take Profit"]) > 0;

    if (hasProduct && hasBalance && hasRisk && hasStopLoss && hasTakeProfit) {
      handleCalculate(false, true);
    }
  }, [
    settings["Product"], 
    settings["Running Balance"], 
    settings["Initial Balance"],
    settings["Risk Percentage"],
    settings["Risk Dollar Amount"],
    settings["Stop Loss"],
    settings["Take Profit"],
    settings["Stop Loss Mode"],  
    settings["Take Profit Mode"],
    assetClass
  ]);

useEffect(() => {
  const load = async () => {
    try {
      const data = await getSettings(userId, accountId);
      if (data) {
        setSettings(prev => ({ ...prev, ...data }));
        if (data.AssetClass) {
          setAssetClass(data.AssetClass);
          if (data.AssetClass === "Forex" && (!data["Minimum Contracts"] || data["Minimum Contracts"] > 0.9)) {
            setSettings(p => ({ ...p, "Minimum Contracts": 0.01 }));
          }
          // [!code ++] FIX: Safety logic for Stocks contracts (default to 0.0001)
          if (data.AssetClass === "Stocks" && (!data["Minimum Contracts"] || data["Minimum Contracts"] >= 1)) {
             setSettings(p => ({ ...p, "Minimum Contracts": 0.0001 }));
          }
        }
        setJournal(prev => ({
          ...prev,
          Product: data.Product || prev.Product,
          Timeframe: data["Default Timeframe"] || prev.Timeframe,
          "Entry Strategy": data["Default Entry Strategy"] || "",
          "Exit Strategy": data["Default Exit Strategy"] || "",
          "Session": data["Default Session"] || "",
        }));
      }
      const rows = await fetchJournalRows(userId, accountId);
      const entries = [...new Set(rows.map(r => r["Entry Strategy"]).filter(Boolean))];
      const exits = [...new Set(rows.map(r => r["Exit Strategy"]).filter(Boolean))];
      setEntryStrategies(entries);
      setExitStrategies(exits);
    } catch (err) { console.error("Load error:", err); }
  };
  load();
}, [userId, accountId]);

  useEffect(() => {
    if (!userId) return;
    refreshDailyBars();
    const initial = Number(settings["Initial Balance"] || 0);
    const goal = Number(settings["Account Goal"] || 0);
    const running = Number(settings["Running Balance"] || 0);
    let pct = 0;
    if (goal > initial) pct = ((running - initial) / (goal - initial)) * 100;
    setProgress(Math.max(0, Math.min(100, pct)));
  }, [userId, accountId, settings["Daily Goal"], settings["Daily Loss Limit"], settings["Running Balance"]]);

const refreshDailyBars = async () => {
    const rows = await fetchJournalRows(userId, accountId);
    const today = getTodayLocalISO();
    const todayRows = rows.filter(r => (r.Date || "").startsWith(today));
    const net = todayRows.reduce((sum, r) => sum + (Number(r["Actual Reward"]) || 0), 0);
    const goal = Number(settings["Daily Goal"] || 0);
    const limit = Number(settings["Daily Loss Limit"] || 0);
    const done = Math.max(0, net);
    const lost = Math.max(0, -net);
    setDailyGoalProg({ done, target: goal, pct: goal > 0 ? (done/goal)*100 : 0 }); 
    setDailyLossProg({ lost, limit, pct: limit > 0 ? (lost/limit)*100 : 0 });
    return { net, goal, limit }; 
  };

const handleAssetClassChange = async (ac) => {
  setAssetClass(ac);
  const newMin = ac === "Forex" ? 0.01 : (ac === "Stocks" ? 0.0001 : 1);
  const updatedSettings = { 
    ...settings, 
    AssetClass: ac, 
    Product: "", 
    "Minimum Contracts": newMin 
  };
  setSettings(updatedSettings);
  setCalculatedSummary(null);
  try {
    await updateSettings(userId, accountId, updatedSettings);
  } catch (err) {
    console.error("Failed to save platform preference:", err);
  }
};

  function getDailyGoalMessage(timeZone = DEFAULT_TIME_ZONE) {
  const now = new Date();
  const weekday = new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    ...(timeZone ? { timeZone } : {})
  }).format(now);

  if (weekday === 'Sunday' || weekday === 'Monday') {
    return "You are off to a great start of the week. Keep the momentum, consider stopping for the day and enjoy the things that matter outside of trading!";
  }
  if (weekday === 'Tuesday' || weekday === 'Wednesday') {
    return "You have the rest of the week!! The market will always be there, but this moment in time will be gone forever. Don't forget to enjoy the things that matter outside of trading!";
  }
  if (weekday === 'Thursday') {
    return "Go and enjoy the rest of your day!";
  }
  if (weekday === 'Friday') {
    return "Today is Capital Preservation Friday! Consider stopping for the day and get ready for a great weekend!!";
  }
  return "Go and enjoy the rest of your day!";
}

const handleSettingChange = async (key, val) => {
  setSettings(prev => {
    const next = { ...prev, [key]: val };
    if (key === "Initial Balance" && !prev["Running Balance"]) next["Running Balance"] = val;
    return next;
  });
  setCalculatedSummary(null);
  if (key === "Product" && val !== "") {
    try {
      await updateSettings(userId, accountId, { 
        ...settings, 
        [key]: val,
        AssetClass: assetClass 
      });
    } catch (err) {
      console.error("Failed to auto-save product selection:", err);
    }
  }
  // We call runSummaryOnly to update projections instantly while typing, using the new value
  runSummaryOnly({ [key]: val });
};
  const handleJournalChange = (key, val) => setJournal(prev => ({ ...prev, [key]: val }));

  // --- HELPER TO UNIFY CALCULATION LOGIC ---
  const calculateProjections = (s, assetClassOverride) => {
      const ac = assetClassOverride || assetClass;
      
      const balance = Number(s["Running Balance"] ?? s["Initial Balance"] ?? 0);
      const riskVal = Number(s["Risk Mode"] === "$" ? s["Risk Dollar Amount"] : s["Risk Percentage"]);
      const riskDollar = s["Risk Mode"] === "$" ? riskVal : (balance * (riskVal / 100));
      
      const stopLoss = Number(s["Stop Loss"] || 0);
      const takeProfit = Number(s["Take Profit"] || 0);
      
      let minSize = Number(s["Minimum Contracts"]);
      if (!minSize || minSize <= 0) {
        if (ac === "Forex") minSize = 0.01;
        else if (ac === "Stocks") minSize = 0.0001;
        else minSize = 1;
      }

      const maxSize = Number(s["Maximum Contracts"] || 1000);
      const entryPrice = Number(s["Entry Price"] || 0);
      const product = s["Product"] || "";
      const multiplier = getMultiplier(ac, product, s);

      // --- STOCKS SPECIFIC LOGIC ---
      let calculatedStopAmt = stopLoss; 
      let calculatedTpAmt = takeProfit;
      let calculatedSize = 0;

      if (ac === "Stocks") {
        const slMode = s["Stop Loss Mode"] || "%";
        const tpMode = s["Take Profit Mode"] || "%";

        if (slMode === "%" && entryPrice > 0) calculatedStopAmt = entryPrice * (stopLoss / 100);
        else calculatedStopAmt = stopLoss;

        if (tpMode === "%" && entryPrice > 0) calculatedTpAmt = entryPrice * (takeProfit / 100);
        else calculatedTpAmt = takeProfit;

        if (entryPrice > 0) calculatedSize = Math.floor((riskDollar / entryPrice) * 10000) / 10000;
      } 
      else {
        if (stopLoss > 0 && multiplier > 0) {
          let raw = riskDollar / (stopLoss * multiplier);
          calculatedSize = ac === "Forex" ? (Math.floor(raw * 100) / 100) : Math.floor(raw);
        }
      }

      calculatedSize = Math.max(minSize, Math.min(maxSize, calculatedSize));
      
      // Underfunded Check
      let minRisk = 0;
      if (ac === "Stocks") {
          if (entryPrice > 0 && (minSize * entryPrice) > riskDollar) calculatedSize = 0;
      } else {
          minRisk = minSize * stopLoss * multiplier;
          if (minRisk > riskDollar) calculatedSize = 0;
      }

      return {
          ac, balance, riskDollar, calculatedSize, minSize, maxSize, 
          calculatedStopAmt, calculatedTpAmt, entryPrice, stopLoss, takeProfit, multiplier
      };
  };

  // --- CALCULATION ENGINE ---
  const handleCalculate = async (save = true, silent = false) => {
    setSavingSettings(true);
    try {
      if (save) await updateSettings(userId, accountId, { ...settings, AssetClass: assetClass });

      const calc = calculateProjections(settings, assetClass);
      
      let totalRisk = 0;
      let targetReward = 0;
      const validSize = calc.calculatedSize > 0 ? calc.calculatedSize : calc.minSize;

      if (assetClass === "Stocks") {
        totalRisk = validSize * calc.calculatedStopAmt; 
        targetReward = validSize * calc.calculatedTpAmt;
      } else {
        totalRisk = validSize * calc.stopLoss * calc.multiplier;
        targetReward = validSize * calc.takeProfit * calc.multiplier;
      }

      let maxStopLossSizeForMin = 0;
      if (assetClass !== "Stocks" && validSize > 0 && calc.multiplier > 0) {
          maxStopLossSizeForMin = Math.floor(calc.riskDollar / (validSize * calc.multiplier));
      }

      let riskRewardRatio = "";
      if (assetClass === "Stocks") {
         if (calc.calculatedStopAmt > 0 && calc.calculatedTpAmt > 0) {
             const ratio = calc.calculatedTpAmt / calc.calculatedStopAmt;
             riskRewardRatio = `${ratio % 1 === 0 ? ratio : ratio.toFixed(2)}:1`;
         }
      } else {
         if (calc.stopLoss > 0 && calc.takeProfit > 0) {
             const ratio = calc.takeProfit / calc.stopLoss;
             riskRewardRatio = `${ratio % 1 === 0 ? ratio : ratio.toFixed(2)}:1`;
         }
      }

      setCalculatedSummary({
        size: calc.calculatedSize,
        riskDollar: calc.riskDollar,
        totalRisk,
        targetReward,
        unit: getUnitLabel(assetClass),
        multiplier: calc.multiplier,
        pricePerTick: calc.multiplier,   
        maxStopLossSizeForMin,      
        minContracts: calc.minSize,      
        percentOfBalance: calc.riskDollar, 
        stopLossVal: calc.stopLoss,
        takeProfitVal: calc.takeProfit,
        stopAmt: calc.calculatedStopAmt,
        tpAmt: calc.calculatedTpAmt,
        entryPrice: calc.entryPrice, 
        rr: riskRewardRatio,
        riskRewardRatio: riskRewardRatio 
      });

      if (calc.calculatedSize === 0) {
         setLastTotalRisk(totalRisk);
         const bal = Number(settings["Running Balance"] || settings["Initial Balance"] || 1);
         const pct = (totalRisk / bal) * 100;
         setLastActualRiskPercent(pct.toFixed(2));
         
         if (!silent) {
            setShowZeroContractsPopup(true);
         }
      }

      setWinProjection(getCompoundingProjection({
        assetClass, balance: calc.balance, goal: Number(settings["Account Goal"]||0), 
        riskPercent: Number(settings["Risk Percentage"]||0), riskMode: settings["Risk Mode"], riskDollar: Number(settings["Risk Dollar Amount"]||0),
        stopLoss: (assetClass==="Stocks"?calc.calculatedStopAmt:calc.stopLoss), 
        takeProfit: (assetClass==="Stocks"?calc.calculatedTpAmt:calc.takeProfit),
        maxContracts: calc.maxSize, minContracts: calc.minSize, multiplier: calc.multiplier, entryPrice: calc.entryPrice
      }));

      setLossProjection(getLossProjection({
        assetClass, balance: calc.balance, 
        riskPercent: Number(settings["Risk Percentage"]||0), riskMode: settings["Risk Mode"], riskDollar: Number(settings["Risk Dollar Amount"]||0),
        stopLoss: (assetClass==="Stocks"?calc.calculatedStopAmt:calc.stopLoss),
        maxContracts: calc.maxSize, minContracts: calc.minSize, multiplier: calc.multiplier, entryPrice: calc.entryPrice
      }));

      setJournal(prev => ({
            ...prev, 
            // --- Fields to Clear ---
            "Actual Balance": "", 
            "Win": false, 
            "Loss": false, 
            "Notes": "", 
            "Movement": "", 
            "Potential": "",
            "Size": calc.calculatedSize ?? "", 
            "Did I follow my Rules?": "", 
            "Mood_Mindset": "", 
            "Mood": "", 
            "Lesson": "", 
            "LogLesson": false,
            "Position": "",
            "Entry Time": "",
            "Exit Time": "",
            "Entry Price": "",
            "Exit Price": "",
            "Grade": "",
            "Ticks":"",
            "Links_Screenshots": "",
            
            // --- Fields to Reset to Default (if exists) ---
            "Session": settings["Default Session"] || "",
            "Timeframe": settings["Default Timeframe"] || "",
            "Entry Strategy": settings["Default Entry Strategy"] || "",
            "Exit Strategy": settings["Default Exit Strategy"] || "",
          }));

    } catch (e) { console.error(e); }
    setSavingSettings(false);
  };

  const TICK_SIZES = {
    YM: 1, MYM: 1, ES: 0.25, MES: 0.25, NQ: 0.25, MNQ: 0.25,
    RTY: 0.1, M2K: 0.1, GC: 0.1, MGC: 0.1, CL: 0.01, MCL: 0.01,
    NG: 0.001, MNG: 0.001, SI: 0.005, SIL: 0.005, BTC: 5, MBT: 5,
  };

  function getTickSizeForProduct(productStr) {
    if (!productStr) return null;
    const firstPart = String(productStr).split(" - ")[0];
    const sym = firstPart.split("·")[0].trim().toUpperCase();
    return TICK_SIZES[sym] ?? null;
  }

  function computeTicksFromPricesAbs(productStr, entryPriceRaw, exitPriceRaw) {
    const tickSize = getTickSizeForProduct(productStr);
    const entryStr = entryPriceRaw != null ? String(entryPriceRaw).trim() : "";
    const exitStr  = exitPriceRaw  != null ? String(exitPriceRaw).trim()  : "";
    if (!entryStr || !exitStr || !tickSize || tickSize <= 0) return null;
    const entry = Number(entryStr);
    const exit  = Number(exitStr);
    if (!Number.isFinite(entry) || !Number.isFinite(exit)) return null;
    const diff = Math.abs(exit - entry);
    return Math.round(diff / tickSize);
  }

  // --- REFRESH PROJECTIONS HELPER ---
    const runSummaryOnly = (override = {}) => {
      const s = { ...settings, ...override };
      const calc = calculateProjections(s, assetClass);

      setWinProjection(getCompoundingProjection({
          assetClass, 
          balance: calc.balance, 
          goal: Number(s["Account Goal"]||0), 
          riskPercent: Number(s["Risk Percentage"]||0), 
          riskMode: s["Risk Mode"], 
          riskDollar: calc.riskDollar,
          stopLoss: (assetClass==="Stocks" ? calc.calculatedStopAmt : calc.stopLoss), 
          takeProfit: (assetClass==="Stocks" ? calc.calculatedTpAmt : calc.takeProfit),
          maxContracts: calc.maxSize, 
          minContracts: calc.minSize, 
          multiplier: calc.multiplier, 
          entryPrice: calc.entryPrice 
      }));

      setLossProjection(getLossProjection({
          assetClass, 
          balance: calc.balance, 
          riskPercent: Number(s["Risk Percentage"]||0), 
          riskMode: s["Risk Mode"], 
          riskDollar: calc.riskDollar,
          stopLoss: (assetClass==="Stocks" ? calc.calculatedStopAmt : calc.stopLoss),
          maxContracts: calc.maxSize, 
          minContracts: calc.minSize, 
          multiplier: calc.multiplier, 
          entryPrice: calc.entryPrice
      }));
    };

  // --- ADD THIS: Auto-Calculate Ticks/Pips/Balance from Entry & Exit Prices ---
  useEffect(() => {
    const entryVal = parseFloat(journal["Entry Price"]);
    const exitVal = parseFloat(journal["Exit Price"]);

    // Only proceed if both prices are valid numbers
    if (!isNaN(entryVal) && !isNaN(exitVal) && settings["Product"]) {
      let calcTicks = 0;
      const diff = Math.abs(exitVal - entryVal);

      if (assetClass === "Futures") {
        // Use the existing helper to find tick size
        const tickSize = getTickSizeForProduct(settings["Product"]) || 1;
        calcTicks = Math.round(diff / tickSize);
      } else if (assetClass === "Forex") {
        // Use the existing helper for pip size
        const pipSize = getPipSizeForSymbol(settings["Product"]);
        calcTicks = Math.round(diff / pipSize);
      } else {
        // Stocks: Points (Dollars)
        calcTicks = Number(diff.toFixed(2));
      }

      setJournal(prev => {
        // Prevent infinite loops: only update if value is different
        if (Number(prev["Ticks"]) === calcTicks) return prev;

        const next = { ...prev, "Ticks": calcTicks };

        // --- Auto-Update Balance based on calculated Ticks ---
        const prevBal = Number(settings["Running Balance"] || settings["Initial Balance"] || 0);
        
        // Determine Size
        const rawSize = prev["Size"];
        const calcSize = calculatedSummary?.size || 0;
        const size = (rawSize !== "" && rawSize != null) ? Number(rawSize) : calcSize;
        
        // Determine Multiplier
        const mult = calculatedSummary?.multiplier || getMultiplier(assetClass, settings["Product"], settings);

        // Calculate Amount
        const amount = calcTicks * mult * size;
        
        // Apply Win/Loss Logic
        const isLoss = prev["Loss"] === true;
        next["Actual Balance"] = (isLoss ? prevBal - amount : prevBal + amount).toFixed(2);

        return next;
      });
    }
  }, [journal["Entry Price"], journal["Exit Price"], assetClass, settings["Product"]]); // Dependencies ensure this runs when prices change

const handleSaveTrade = async () => {
  const errors = [];

  const abRaw = journal["Actual Balance"];
  // Use a strict check to allow 0 to be a valid entry
  const abIsEmpty = abRaw === "" || abRaw === null || abRaw === undefined;
  const abNum = parseCurrency(abRaw);
  
  if (abIsEmpty || !Number.isFinite(abNum)) errors.push("- Actual Balance");
  if (!journal["Date"]) errors.push("- Date");
  if (!settings["Product"]) errors.push("- Product");
  if (!journal["Win"] && !journal["Loss"]) errors.push("- Win/Loss");
  
  const rawLesson = journal["Lesson"];
  const lessonString = (typeof rawLesson === 'string' ? rawLesson : String(rawLesson || "")).trim();

  const rawSize = journal["Size"];
  const hasManualSize = rawSize !== "" && rawSize !== null && rawSize !== undefined;
  const calculatedVal = Number(calculatedSummary?.contracts ?? calculatedSummary?.size ?? 0);
  const effectiveContracts = hasManualSize ? Number(rawSize) : calculatedVal;

  if (!Number.isFinite(effectiveContracts) || effectiveContracts <= 0) {
     errors.push(`- # of ${getUnitLabel(assetClass)}`);
  }
  if (!calculatedSummary) errors.push("- Click the Calculate button before saving");

  if (errors.length > 0) {
    setMissingFields(errors);
    return;
  }

  setSavingTrade(true);
  popupsAllowedRef.current = true;

  try {
    const tradeNum = await getNextTradeNumber(userId, accountId);
    
    const prevBal = Number(settings["Running Balance"]) || 0;
    const limit = assetClass === "Stocks" ? 1 : 0;
    const rawNewBal = abIsEmpty ? prevBal : parseCurrency(abRaw);
    // Only force the floor if the balance is strictly between 0 and 1.00
    // If it's exactly 0, let it be 0.
    const newBal = (assetClass === "Stocks" && rawNewBal > 0 && rawNewBal < 1) ? 1 : rawNewBal;
    const actualRewardDollar = newBal - prevBal;
    
    const product = settings["Product"] || "";
    const isDidNotTake = String(journal["Position"] || "").trim().toLowerCase() === "did not take";
    let effectiveTicks = null;
    const ticksRaw = journal["Ticks"] || journal["Movement"];
    
    if (ticksRaw !== "" && ticksRaw != null) {
      let val = Number(ticksRaw);
      // Force negative if Loss, Force positive if Win
      if (journal["Loss"]) {
        val = -Math.abs(val);
      } else if (journal["Win"]) {
        val = Math.abs(val);
      }
      effectiveTicks = val;
    } else if (!isDidNotTake) {
      const ticksFromPriceAbs = computeTicksFromPricesAbs(product, journal["Entry Price"], journal["Exit Price"]);
      if (ticksFromPriceAbs != null && Number.isFinite(ticksFromPriceAbs)) {
        effectiveTicks = !!journal["Loss"] ? -ticksFromPriceAbs : ticksFromPriceAbs;
      }
    }

    const pricePerTick = getMultiplier(assetClass, settings["Product"], settings);
    
    // --- FIX START: Calculate effective SL/TP amounts (handling % mode for Stocks) ---
    let effectiveStopLoss = Number(settings["Stop Loss"] || 0);
    let effectiveTakeProfit = Number(settings["Take Profit"] || 0);

    if (assetClass === "Stocks") {
        const entryPrice = Number(settings["Entry Price"] || 0);
        
        if (entryPrice > 0) {
            // If Stop Loss is in %, convert to $ amount per share
            if (settings["Stop Loss Mode"] === "%") {
                effectiveStopLoss = entryPrice * (effectiveStopLoss / 100);
            }
            // If Take Profit is in %, convert to $ amount per share
            if (settings["Take Profit Mode"] === "%") {
                effectiveTakeProfit = entryPrice * (effectiveTakeProfit / 100);
            }
        }
    }
    // --- FIX END ---

    const tradeObj = {};

    SHEET_HEADERS.forEach((header) => {
      if (header === "Trade #") tradeObj[header] = tradeNum;
      else if (header === "tradeNum") tradeObj["tradeNum"] = tradeNum;
      else if (header === "Product") tradeObj[header] = settings["Product"];
      else if (header === "Running Balance") tradeObj[header] = newBal;
      else if (header === "Price Per Tick") tradeObj[header] = pricePerTick;
      else if (header === "Risk To Reward Ratio") {
        // Use effective amounts for R:R calculation
        if (effectiveTakeProfit && effectiveStopLoss) {
          const rr = effectiveTakeProfit / effectiveStopLoss;
          tradeObj[header] = `1:${rr % 1 === 0 ? rr : rr.toFixed(2).replace(/\.?0+$/, "")}`;
        } else tradeObj[header] = "";
      }
      else if (header === "Percent of Balance") {
        const riskMode = String(settings["Risk Mode"] || "%");
        const riskDollarAmt = Number(settings["Risk Dollar Amount"] || 0);
        tradeObj[header] = (riskMode === "$") ? riskDollarAmt : prevBal * (Number(settings["Risk Percentage"] || 0) / 100);
      }
      else if (header === "Stop Loss_Take Profit") {
        tradeObj[header] = settings["Stop Loss"] && settings["Take Profit"] ? `${settings["Stop Loss"]}/${settings["Take Profit"]}` : "";
      }
      else if (header === "Suggested Max # of Contracts") tradeObj[header] = calculatedVal;
      else if (header === "Total Risk") {
        // [!code ++] Use effectiveStopLoss
        tradeObj[header] = effectiveContracts * effectiveStopLoss * pricePerTick;
      }
      else if (header === "Size") tradeObj[header] = effectiveContracts;
      else if (header === "Tcks" || header === "Ticks") tradeObj[header] = effectiveTicks == null ? "" : Number(effectiveTicks);
      else if (header === "Total Target Reward") {
        // [!code ++] Use effectiveTakeProfit
        tradeObj[header] = effectiveContracts * effectiveTakeProfit * pricePerTick;
      }
      else if (header === "Actual Reward") tradeObj[header] = actualRewardDollar;
      else if (header === "Duration (h:mm)") tradeObj[header] = getDurationHHMMSS(journal["Entry Time"], journal["Exit Time"]);
      else if (header === "Entry Time") tradeObj[header] = journal["Entry Time"] ? to24HourTime(journal["Entry Time"]) : "";
      else if (header === "Exit Time") tradeObj[header] = journal["Exit Time"] ? to24HourTime(journal["Exit Time"]) : "";
      else if (header === "Actual Percent Growth") {
        
        const rawType = String(
            settings["Account Type"] || 
            settings.type || 
            settings.Platform || 
            settings["Trade Mode"] || 
            ""
        ).toLowerCase();
        
        const isMixed = rawType.includes("mixed");
        const isExplicitProp = settings.isProp || settings.isPropFirm || settings.isPropEval || settings.isPropFunded;
        
        const maxLoss = Number(settings["Maximum Loss Limit"] || settings["Max Loss Limit"] || 0);

        let baseBalance = prevBal;

        if (isExplicitProp && maxLoss > 0) {
            baseBalance = Math.max(1, prevBal - maxLoss);
        }

        tradeObj[header] = baseBalance > 0 
          ? ((actualRewardDollar / baseBalance) * 100).toFixed(2) + "%" 
          : "0.00%";
      }
      else if (header === "Actual Risk to Reward") {
        // [!code ++] Use effectiveStopLoss
        const riskDollar = effectiveContracts * effectiveStopLoss * pricePerTick;
        if (riskDollar > 0 && actualRewardDollar !== 0) {
          const ratio = Math.abs(actualRewardDollar) / riskDollar;
          tradeObj[header] = `${actualRewardDollar < 0 ? "-" : ""}${Number.isInteger(ratio) ? ratio : ratio.toFixed(2)}:1`;
        } else tradeObj[header] = "";
      }
      else if (header === "Lesson") tradeObj[header] = lessonString;
      else tradeObj[header] = journal[header] ?? "";
    });

    tradeObj["tradeNum"] = tradeNum;
    tradeObj["Win"] = journal["Win"];
    tradeObj["Loss"] = journal["Loss"];
    tradeObj["LogLesson"] = journal["LogLesson"];
    tradeObj["Account Type"] = assetClass;
    // [!code ++] Use effectiveStopLoss for the final logic check
    const logicTotalRisk = effectiveContracts * effectiveStopLoss * pricePerTick;
    tradeObj["Total Risk"] = logicTotalRisk;

    await appendTrade(userId, accountId, tradeObj);

    if (journal["LogLesson"] && lessonString) {
      await appendLesson(lessonString);
    }

    const nextSettings = { ...settings, "Running Balance": newBal };
    await updateSettings(userId, accountId, nextSettings);
    setSettings(nextSettings);
    runSummaryOnly({ "Running Balance": newBal });

    try {
      const me = await getSettings(userId, accountId);
      if (me?.CopyTrade?.role === "leader" || me?.settings?.CopyTrade?.role === "leader") {
        const { copyTradeToFollowers } = await import("../services/CopyTradeService");
        await copyTradeToFollowers({
          userId,
          leaderAccountId: accountId,
          leaderTrade: tradeObj,
          delta: actualRewardDollar,
          accountType: "futures"
        });
      }
    } catch (e) { console.error("[CopyTrade] failed", e); }

    // DISABLED POPUP: setCurrentPopup({ id: 'tradeSaved' });
     setCurrentPopup({ id: 'tradeSaved' });

    try {
      // --- DISABLED ALL ANALYSIS POPUPS BELOW (Except Zero Contracts which is not here) ---
      
      const today = getTodayLocalISO();
      /*
      let currentSettings = { ...nextSettings }; 

      const rawHistory = await fetchJournalRows(userId, accountId);
      const cleanHistory = rawHistory.filter(r => String(r.tradeNum) !== String(tradeNum));
      const allRows = [tradeObj, ...cleanHistory]; 

      const rowTimestamp = (row) => {
        const d = row?.["Date"];
        if (!d) return 0;
        const dt = (d.includes && d.includes("-")) ? new Date(d) : (d.seconds ? new Date(d.seconds * 1000) : new Date());
        return dt.getTime();
      };
      
      const sortedRows = allRows.sort((a, b) => {
         const ta = Number(a?.tradeNum ?? 0);
         const tb = Number(b?.tradeNum ?? 0);
         if (ta !== tb) return tb - ta;
         return rowTimestamp(b) - rowTimestamp(a);
      });

      const markShown = async (key) => {
          currentSettings[key] = today;
          await updateSettings(userId, accountId, { [key]: today });
          setSettings(prev => ({ ...prev, [key]: today }));
      };

      const maxLossLimit = Number(settings["Maximum Loss Limit"] || settings["Max Loss Limit"] || 0);
      const initBal = Number(settings["Initial Balance"] || 0);
      const isProp = (initBal > 0 && maxLossLimit > 0);
      const blownThreshold = isProp ? (initBal - maxLossLimit) : 0; 

      if (newBal <= blownThreshold) {
         if (currentSettings["MaxLossLimitBlownShownDate"] !== today) {
             setCurrentPopup({ id: 'maxLossLimitBlown', data: { endingBalance: newBal, startingBalance: prevBal } });
             await markShown("MaxLossLimitBlownShownDate");
         }
         return; 
      }

      const localQueue = [];
      const todayTrades = sortedRows.filter(t => (t["Date"] || "").startsWith(today));
      const todayTradesAsc = [...todayTrades].sort((a,b) => (Number(a.tradeNum||0) - Number(b.tradeNum||0)));

      const dailyLossLim = Number(settings["Daily Loss Limit"] || 0);
      const maxLosses = Number(settings["Max Loss Trades Per Day"] || 0);
      const maxTrades = Number(settings["Max Trades Per Day"] || 0);
      const dailyGoalAmount = Number(settings["Daily Goal"] || 0);
      
      const netPnL = todayTrades.reduce((acc, r) => acc + (Number(r["Actual Reward"]) || 0), 0);

      let goalWasReachedToday = false;
      let runningDailyPnL = 0;
      if (dailyGoalAmount > 0) {
          for (const t of todayTradesAsc) {
              runningDailyPnL += (Number(t["Actual Reward"]) || 0);
              if (runningDailyPnL >= dailyGoalAmount) {
                  goalWasReachedToday = true;
                  break; 
              }
          }
      }

      const isLoss = r => (r && (r.Loss === true || String(r.Loss).toLowerCase() === "true"));
      const isWin = r => (r && (r.Win === true || String(r.Win).toLowerCase() === "true"));
      const followedRules = (journal["Did I follow my Rules?"] === "Yes");
      const tradeGrowth = prevBal > 0 ? (actualRewardDollar / prevBal) * 100 : 0;

      if (settings["Max Trades Per Day Toggle"] && maxTrades > 0 && todayTrades.length > maxTrades && currentSettings["OvertradingPopupShownDate"] !== today) {
          localQueue.push({ id: 'overtradingPopup', data: { maxTradesPerDay: maxTrades } });
          await markShown("OvertradingPopupShownDate");
      }

      if (currentSettings["LossLimitWarningShownDate"] !== today && dailyLossLim > 0 && netPnL <= -dailyLossLim) {
          localQueue.push({ id: 'lossLimitWarning', data: { lossForDay: Math.abs(netPnL), lossLimitAmount: dailyLossLim } });
          await markShown("LossLimitWarningShownDate");
      }

      if (settings["Max Loss Trades Per Day Toggle"] && maxLosses > 0 && todayTrades.filter(isLoss).length >= maxLosses && currentSettings["TooManyLossesPopupShownDate"] !== today) {
          localQueue.push({ id: 'tooManyLossesPopup', data: { maxLossesPerDay: maxLosses } });
          await markShown("TooManyLossesPopupShownDate");
      }

      if (actualRewardDollar < 0 && currentSettings["OverRiskWarningShownDate"] !== today) {
          const lossAmt = Math.abs(actualRewardDollar);
          if (logicTotalRisk >= 0 && lossAmt > (logicTotalRisk + 0.01)) { 
              localQueue.push({ id: 'overRiskWarning', data: { riskAmount: logicTotalRisk, amountLost: lossAmt } });
              await markShown("OverRiskWarningShownDate");
          }
      }

      const safeMultiplier = pricePerTick || 1; 
      
      const localLossProj = getLossProjection({
        assetClass: assetClass || "Futures", 
        balance: newBal, 
        riskPercent: Number(settings["Risk Percentage"]||0), 
        riskMode: settings["Risk Mode"], 
        riskDollar: Number(settings["Risk Dollar Amount"]||0),
        stopLoss: Number(settings["Stop Loss"]||0),
        maxContracts: Number(settings["Maximum Contracts"]||1000), 
        minContracts: Number(settings["Minimum Contracts"]||1), 
        multiplier: safeMultiplier, 
        entryPrice: Number(settings["Entry Price"]||0)
      });

      const initBalForRuin = Number(settings["Initial Balance"] || 0);
      const ruinThreshold = initBalForRuin * 0.25; 

      if (initBalForRuin > 0 && newBal <= ruinThreshold && currentSettings["RiskOfRuinWarningShownDate"] !== today) {
          localQueue.push({ id: 'riskOfRuinWarning' });
          await markShown("RiskOfRuinWarningShownDate");
      }
      else if (newBal > ruinThreshold && currentSettings["RiskOfRuinWarningShownDate"] === today) {
          const resetSettings = { ...currentSettings, "RiskOfRuinWarningShownDate": "" };
          await updateSettings(userId, accountId, resetSettings);
          setSettings(prev => ({ ...prev, "RiskOfRuinWarningShownDate": "" }));
          currentSettings["RiskOfRuinWarningShownDate"] = ""; 
      }

      const last3 = sortedRows.slice(0, 3);

      if (currentSettings["ThreeLossWarningShownDate"] !== today && last3.length === 3 && last3.every(isLoss)) {
          localQueue.push({ 
            id: 'threeLossWarning', 
            data: { 
              message: "You've lost 3 trades in a row.\n\nTake a break! \n\nTake some time to evaluate what is really going on. Make sure you are fresh and have a clear mind before taking another trade.\n\nThe Key is to avoid going on tilt and revenge trading.\n\n Trading is 80% Trading Psychology. This may be a good time to watch some psychology videos.  The video below from Psychology of Profits is a good one to start with.", 
              videoLink: "https://www.youtube.com/watch?v=6vZG4epFEOM" 
            } 
          });
          await markShown("ThreeLossWarningShownDate");
      } 
      else if (
          currentSettings["TwoLossAfterGoalWarningShownDate"] !== today && 
          dailyGoalAmount > 0 && 
          goalWasReachedToday && 
          last3.length >= 2 && 
          isLoss(last3[0]) && isLoss(last3[1])
      ) {
           localQueue.push({ id: 'twoLossAfterGoalWarning', data: { message: "You have taken 2 losses after reaching your daily goal.\n\nYou could easily go on tilt if you continue to lose trying to get it back. \n\nConsider stopping for the day to protect your gains." } });
           await markShown("TwoLossAfterGoalWarningShownDate");
      }
      else if (currentSettings["TwoLossWarningShownDate"] !== today && last3.length >= 2 && isLoss(last3[0]) && isLoss(last3[1])) {
          localQueue.push({ id: 'twoLossWarning' });
          await markShown("TwoLossWarningShownDate");
      }

      if (journal["Win"] === true && tradeGrowth >= 10 && !followedRules && currentSettings["Win10NoRulesPopupShownDate"] !== today) {
          localQueue.push({ 
              id: 'win10NoRulesPopup', 
              data: { message: "Winning BIG is always exciting!\n\n However you want to be careful. Having a big win without following your rules can be dangerous for your mindset.\n\nContinuing to do this could rewire your brain to seek dopamine hits instead of the true accomplishment of following your process.\n\nThis is another way to easily lose control and go on tilt.\n\nTreat this as a neutral event, and be mindful not to get hooked by big wins that happen outside your plan.\n\nDiscipline > excitement!" } 
          });
          await markShown("Win10NoRulesPopupShownDate");
      }

      const highMoodToday = todayTrades.filter(t => {
          const v = Number(t["Mood_Mindset"] || t["Mood"] || 0);
          return v === 4 || v === 5;
      });
      if (highMoodToday.length >= 3 && currentSettings["MoodVolatilityPopupShownDate"] !== today) {
          localQueue.push({ id: 'moodVolatilityPopup' });
          await markShown("MoodVolatilityPopupShownDate");
      }

      const ddToggle = (settings["Drawdown Limit % Toggle"] ?? true);
      if (ddToggle && currentSettings["DrawdownPopupShownDate"] !== today && sortedRows.length > 5) {
          const balances = sortedRows.map(r => Number(r["Actual Balance"] || r["Running Balance"])).filter(b => !isNaN(b) && b > 0);
          const peak = Math.max(...balances, initBal);
          const currentDrawdown = (peak - newBal);
          const currentDrawdownPct = peak > 0 ? (currentDrawdown / peak) * 100 : 0;
          const limitPct = Number(settings["Drawdown Limit %"] || 20);

          if (currentDrawdownPct >= limitPct) {
              localQueue.push({ 
                  id: 'drawdownWarning', 
                  data: { drawdownPct: currentDrawdownPct, drawdownAmount: currentDrawdown, drawdownLimitPct: limitPct }
              });
              await markShown("DrawdownPopupShownDate");
          }
      }

      const lessonsTodayCount = todayTrades.filter(t => 
          (t["LogLesson"] === true || String(t["LogLesson"]).toLowerCase() === "true")
      ).length;
      
      if (lessonsTodayCount >= 3 && currentSettings["LessonsLearnedStreakWarningShownDate"] !== today) {
           localQueue.push({ id: 'lessonsLearnedStreakWarning' });
           await markShown("LessonsLearnedStreakWarningShownDate");
      }

      if (Number(settings["Account Goal"] || 0) > 0 && newBal >= Number(settings["Account Goal"]) && currentSettings["AccountGoalReachedShownDate"] !== today) {
          localQueue.push({ id: 'accountGoalReached' });
          await markShown("AccountGoalReachedShownDate");
      }
      else if (dailyGoalAmount > 0 && netPnL >= dailyGoalAmount && currentSettings["DailyGoalPopupShownDate"] !== today && !isLoss(journal)) {
          localQueue.push({ id: 'dailyGoalPopup', data: { dailyGoal: dailyGoalAmount } });
          await markShown("DailyGoalPopupShownDate");
      }

      if (journal["Win"] === true && tradeGrowth >= 20 && followedRules && currentSettings["Win10WithRulesPopupShownDate"] !== today) {
          localQueue.push({ id: 'win10WithRulesPopup', data: { message: "Congratulations! You followed your rules and had a BIG win! \n\nDon't get over confident, stay consistent and continue following your rules. \n\nContinue being Awesome!", confetti: true } });
          await markShown("Win10WithRulesPopupShownDate");
      }

      const last5 = sortedRows.slice(0, 5);
      if (last5.length === 5 && last5.every(isWin)) {
          const sixthTrade = sortedRows[5]; 
          const isStartOfStreak = !sixthTrade || !isWin(sixthTrade);
          if (isStartOfStreak) {
              localQueue.push({ id: 'consecutiveWinsPopup' });
          }
      }

      if (localQueue.length > 0) {
          setPopupQueue(prev => [...prev, ...localQueue]);
      }
      */

    } catch (analyzeErr) { console.error("Analysis Error:", analyzeErr); }

    setJournal(prev => ({
      ...prev, "Actual Balance": "", "Win": false, "Loss": false, "Notes": "", "Movement": "", "Potential": "",
      "Size": calculatedVal ?? "", 
      "Did I follow my Rules?": "", "Mood_Mindset": "", "Mood": "", "Lesson": "", "LogLesson": false,
    }));

  } catch (err) { console.error("handleSaveTrade error:", err); } finally { setSavingTrade(false); }
};

const closeCurrentPopup = () => {
    const wasTradeSaved = currentPopup?.id === 'tradeSaved';
    setCurrentPopup(null);

    if (wasTradeSaved && pendingWarning) {
        setCurrentPopup(pendingWarning);
        setPendingWarning(null);
        return;
    }

    if (popupQueue.length > 0) {
        const next = popupQueue[0];
        setPopupQueue(prev => prev.slice(1));
        setCurrentPopup(next);
    }
};
const isDidNotTake =
  (journal["Position"] || "").trim().toLowerCase() === "did not take";
const renderSummaryContent = () => {
    if (!calculatedSummary) return <div className="text-center text-gray-500 py-4">Click Calculate to see summary.</div>;

    return (
      <div className="space-y-1">
        {assetClass === "Futures" && (
           <SummaryRow label="Price Per Tick" value={calculatedSummary.pricePerTick ? formatCurrency(calculatedSummary.pricePerTick) : ""} />
        )}
        
        <SummaryRow 
            label="Reward to Risk Ratio" 
            value={calculatedSummary.riskRewardRatio || calculatedSummary.rr} 
        />
        
        {assetClass === "Stocks" ? (
           <SummaryRow label="Total Investment" value={formatCurrency(calculatedSummary.riskDollar)} />
        ) : (
           <SummaryRow 
             label={
               settings["Risk Mode"] === "%" 
                 ? `${settings["Risk Percentage"]}% of Balance` 
                 : `Risk Amount ($)`
             } 
             value={formatCurrency(calculatedSummary.riskDollar)} 
           />
        )}

        <SummaryRow 
          label={`Stop Loss / Take Profit`} 
          value={`${calculatedSummary.stopLossVal}${settings["Stop Loss Mode"]==="%"?"%":""} / ${calculatedSummary.takeProfitVal}${settings["Take Profit Mode"]==="%"?"%":""} ${assetClass === "Forex" ? "pips" : ""}`} 
        />

        {assetClass === "Stocks" && calculatedSummary.entryPrice > 0 && (
          <>
            <SummaryRow label="Est. Stop Price" value={formatCurrency(calculatedSummary.entryPrice - calculatedSummary.stopAmt)} />
            <SummaryRow label="Est. Target Price" value={formatCurrency(calculatedSummary.entryPrice + calculatedSummary.tpAmt)} />
          </>
        )}

        <SummaryRow 
                  label={
                    <span className="flex items-center gap-2">
                      {assetClass === "Stocks" ? "Projected Loss (at Stop)" : "Total Risk"}
                      <Tooltip 
                        variant="neutral" 
                        backdrop
                        content={(() => {
                          if (assetClass === "Stocks") {
                            return (
                              <div className="space-y-2">
                                <div className="font-bold text-blue-400">Calculation Details</div>
                                <div>
                                  {formatCurrency(calculatedSummary.stopAmt)} risk/share × {calculatedSummary.size} shares = <strong>{formatCurrency(calculatedSummary.totalRisk)}</strong>
                                </div>
                                <div className="text-xs text-gray-300 bg-gray-800 p-2 rounded">
                                  Note: You are investing <strong>{formatCurrency(calculatedSummary.percentOfBalance)}</strong>, but your projected loss is <strong>{formatCurrency(calculatedSummary.totalRisk)}</strong> because your stop loss is small compared to the share price.
                                </div>
                              </div>
                            );
                          } else {
                            const units = assetClass === "Forex" ? "lots" : "contracts";
                            const move = assetClass === "Forex" ? "pips" : "ticks";
                            const val = calculatedSummary.multiplier || 0; 
                            const usedSize = calculatedSummary.size > 0 ? calculatedSummary.size : calculatedSummary.minContracts;
                            
                            const riskCalc = usedSize * calculatedSummary.stopLossVal * val;
                            const isSafe = riskCalc <= (calculatedSummary.riskDollar + 0.01);

                            return (
                              <div className="space-y-2">
                                <div className="text-sm uppercase font-bold text-blue-400">
                                  How we calculate total risk ({assetClass})
                                </div>
                                
                                {assetClass === "Forex" ? (
                                   <>
                                     <div>Stop Loss (pips) × Suggested Lots × Pip Value</div>
                                     <div>{calculatedSummary.stopLossVal} pips × {usedSize} lots × {formatCurrency(val)} = <strong>{formatCurrency(riskCalc)}</strong></div>
                                   </>
                                ) : (
                                   <>
                                     <div>Size × Stop Loss × Value</div>
                                     <div>{usedSize} {units} × {calculatedSummary.stopLossVal} {move} × {formatCurrency(val)} = <strong>{formatCurrency(riskCalc)}</strong></div>
                                   </>
                                )}

                                <div>Percent of balance ({formatCurrency(calculatedSummary.riskDollar)}): <strong>{formatCurrency(calculatedSummary.riskDollar)}</strong></div>
                                
                                <div className={isSafe ? "text-green-400 font-bold" : "text-red-400 font-bold"}>
                                  {isSafe ? "✓ Total risk ≤ percent of balance" : "✕ Total risk > percent of balance"}
                                </div>
                                
                                <div className="text-xs text-gray-300 pt-2 border-t border-gray-600">
                                  *If suggested size is 0, calculation uses your Minimum {assetClass === "Forex" ? "Lot Size" : "Contracts"} ({calculatedSummary.minContracts}).
                                </div>
                              </div>
                            );
                          }
                        })()}
                      >
                        <span className="text-xs bg-gray-200 dark:bg-gray-700 px-1 rounded cursor-help">?</span>
                      </Tooltip>
                    </span>
                  } 
                  value={formatCurrency(calculatedSummary.totalRisk)} 
                  red={calculatedSummary.size === 0}
                />

        

        <SummaryRow label="Target Reward" value={formatCurrency(calculatedSummary.targetReward)} />

        {(assetClass === "Futures" || assetClass === "Forex") && (
            <SummaryRow 
              label={`Maximum Stop Loss`} 
              value={
                calculatedSummary.maxStopLossSizeForMin > 0 
                ? `${calculatedSummary.maxStopLossSizeForMin} ${assetClass === "Forex" ? "pips" : "ticks"}` 
                : "0"
              } 
            />
        )}

        {assetClass === "Forex" && (
           <SummaryRow label="Position Size (Units)" value={(calculatedSummary.size * 100000).toLocaleString()} />
        )}

        <div className={calculatedSummary.size===0 ? "bg-red-50 dark:bg-red-900/30 rounded p-1" : "bg-blue-50 dark:bg-blue-900/30 rounded p-1"}>
          <SummaryRow 
            label={assetClass === "Forex" ? "Suggested Lot Size" : `Suggested # of ${getUnitLabel(assetClass)}`} 
            value={calculatedSummary.size === 0 ? "0" : calculatedSummary.size} 
            red={calculatedSummary.size===0}
          />
        </div>
      </div>
    );
  };

  const renderFuturesSettings = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="md:self-end">
        <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Product<span className="text-red-500">*</span></label>
        <select 
          value={settings["Product"] || ""} 
          onChange={e => handleSettingChange("Product", e.target.value)} 
          className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 bg-gray-50 focus:ring-2 focus:ring-blue-400"
        >
          <option value="">Select {assetClass}</option>
          {favOpts.length > 0 && (
            <optgroup label="♥ Favorites">
              {favOpts.map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </optgroup>
          )}
          <optgroup label={`All ${assetClass}`}>
            {nonFavOpts.map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </optgroup>
        </select>      </div>
      <div>
        <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Initial Balance</label>
        <input type="text" value={formatCurrency(settings["Initial Balance"])} disabled className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 bg-gray-50" />
      </div>
      <div>
        <div className="flex justify-between mb-1">
          <label className="block font-bold text-gray-800 dark:text-gray-200">Risk</label>
          <div className="flex text-xs border rounded overflow-hidden border-gray-200 dark:border-gray-600">
            <button className={`px-2 py-1 ${settings["Risk Mode"]==='%'?"bg-blue-600 text-white":"bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"}`} onClick={()=>handleSettingChange("Risk Mode",'%')}>%</button>
            <button className={`px-2 py-1 ${settings["Risk Mode"]==='$'?"bg-blue-600 text-white":"bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"}`} onClick={()=>handleSettingChange("Risk Mode",'$')}>$</button>
          </div>
        </div>
        <input type="number" value={settings["Risk Mode"]==='$' ? settings["Risk Dollar Amount"] : settings["Risk Percentage"]} onChange={e=>handleSettingChange(settings["Risk Mode"]==='$'?"Risk Dollar Amount":"Risk Percentage", e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400" />
      </div>
      
      <div>
        <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Stop Loss (Ticks)</label>
        <input type="number" value={settings["Stop Loss"]||""} onChange={e=>handleSettingChange("Stop Loss",e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400"/>
      </div>
      <div>
        <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Take Profit (Ticks)</label>
        <input type="number" value={settings["Take Profit"]||""} onChange={e=>handleSettingChange("Take Profit",e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400"/>
      </div>
      <div>
        <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Account Goal</label>
        <input type="number" value={settings["Account Goal"]||""} onChange={e=>handleSettingChange("Account Goal",e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-green-400"/>
      </div>

      <div>
        <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Running Balance</label>
        <input 
          type="number" 
          /* CHANGE THIS LINE: Use ?? instead of || */
          value={settings["Running Balance"] ?? ""} 
          onChange={e=>handleSettingChange("Running Balance",e.target.value)} 
          className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-green-400"
        />
      </div>
        <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200"># Trades to Goal</label>
            <input
              type="number"
              value={calculatedSummary && winProjection ? winProjection.tradeCount : ""}
              readOnly
              disabled
              className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-500 dark:text-white"
            />
          </div>

          <div className="flex items-end">
            <button
              type="button"
              className={`w-full h-[42px] bg-blue-600 dark:bg-blue-700 text-white rounded-xl px-3 py-2 font-semibold hover:bg-blue-700 hover:dark:bg-blue-800 transition ${
                !calculatedSummary ? "opacity-50 cursor-not-allowed pointer-events-none" : ""
              }`}
              onClick={() => setProjectionOpen(true)}
              disabled={!calculatedSummary}
            >
              Win Projections
            </button>
          </div>

          <div className="flex items-end">
            <button
              type="button"
              className={`w-full h-[42px] bg-red-600 dark:bg-red-700 text-white rounded-xl px-3 py-2 font-semibold hover:bg-red-700 hover:dark:bg-red-800 transition ${
                !calculatedSummary ? "opacity-50 cursor-not-allowed pointer-events-none" : ""
              }`}
              onClick={() => setLossProjectionOpen(true)}
              disabled={!calculatedSummary}
            >
              Loss Projections
            </button>
          </div>
        </div>
      </div>
  );

  const renderStocksSettings = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="md:self-end">
        <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">
          Ticker Symbol<span className="text-red-500">*</span>
        </label>
        <input 
          type="text" 
          placeholder="e.g. AAPL" 
          value={settings["Product"] || ""} 
          onChange={e => handleSettingChange("Product", e.target.value.toUpperCase())} 
          className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 bg-gray-50 focus:ring-2 focus:ring-blue-400"
        />
      </div>
      <div className="md:self-end">
        <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Est. Entry Price ($)<span className="text-red-500">*</span></label>
        <input type="number" value={settings["Entry Price"]||""} onChange={e=>handleSettingChange("Entry Price",e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 bg-gray-50 focus:ring-2 focus:ring-blue-400"/>
      </div>
      <div>
        <div className="flex justify-between mb-1">
          <label className="block font-bold text-gray-800 dark:text-gray-200">Investment Amount</label>
          <div className="flex text-xs border rounded overflow-hidden border-gray-200 dark:border-gray-600">
            <button className={`px-2 py-1 ${settings["Risk Mode"]==='$'?"bg-blue-600 text-white":"bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"}`} onClick={()=>handleSettingChange("Risk Mode",'$')}>$</button>
            <button className={`px-2 py-1 ${settings["Risk Mode"]==='%'?"bg-blue-600 text-white":"bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"}`} onClick={()=>handleSettingChange("Risk Mode",'%')}>%</button>
          </div>
        </div>
        <input type="number" value={settings["Risk Mode"]==='$' ? settings["Risk Dollar Amount"] : settings["Risk Percentage"]} onChange={e=>handleSettingChange(settings["Risk Mode"]==='$'?"Risk Dollar Amount":"Risk Percentage", e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400" />
      </div>

      <div>
        <div className="flex justify-between mb-1">
          <label className="block font-bold text-gray-800 dark:text-gray-200">Stop Loss</label>
          <div className="flex text-xs border rounded overflow-hidden border-gray-200 dark:border-gray-600">
            <button className={`px-2 py-1 ${settings["Stop Loss Mode"]==='$'?"bg-blue-600 text-white":"bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"}`} onClick={()=>handleSettingChange("Stop Loss Mode",'$')}>$</button>
            <button className={`px-2 py-1 ${settings["Stop Loss Mode"]==='%'?"bg-blue-600 text-white":"bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"}`} onClick={()=>handleSettingChange("Stop Loss Mode",'%')}>%</button>
          </div>
        </div>
        <input type="number" value={settings["Stop Loss"]||""} onChange={e=>handleSettingChange("Stop Loss",e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400"/>
      </div>
      <div>
        <div className="flex justify-between mb-1">
          <label className="block font-bold text-gray-800 dark:text-gray-200">Take Profit</label>
          <div className="flex text-xs border rounded overflow-hidden border-gray-200 dark:border-gray-600">
            <button className={`px-2 py-1 ${settings["Take Profit Mode"]==='$'?"bg-blue-600 text-white":"bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"}`} onClick={()=>handleSettingChange("Take Profit Mode",'$')}>$</button>
            <button className={`px-2 py-1 ${settings["Take Profit Mode"]==='%'?"bg-blue-600 text-white":"bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"}`} onClick={()=>handleSettingChange("Take Profit Mode",'%')}>%</button>
          </div>
        </div>
        <input type="number" value={settings["Take Profit"]||""} onChange={e=>handleSettingChange("Take Profit",e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400"/>
      </div>
      <div>
        <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Initial Balance</label>
        <input type="text" value={formatCurrency(settings["Initial Balance"])} disabled className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 bg-gray-50" />
      </div>

      <div>
        <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Account Goal</label>
        <input type="number" value={settings["Account Goal"]||""} onChange={e=>handleSettingChange("Account Goal",e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-green-400"/>
      </div>
      <div>
        <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Running Balance</label>
        <input 
          type="number" 
          /* CHANGE THIS LINE: Use ?? instead of || */
          value={settings["Running Balance"] ?? ""} 
          onChange={e=>handleSettingChange("Running Balance",e.target.value)} 
          className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-green-400"
        />
      </div>
   <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
     <div>
       <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200"># Trades to Goal</label>
       <input
         type="number"
         value={calculatedSummary && winProjection ? winProjection.tradeCount : ""}
         readOnly
         disabled
         className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-500 dark:text-white"
       />
     </div>

     <div className="flex items-end">
       <button
         type="button"
         className={`w-full h-[42px] bg-blue-600 dark:bg-blue-700 text-white rounded-xl px-3 py-2 font-semibold hover:bg-blue-700 hover:dark:bg-blue-800 transition ${
           !calculatedSummary ? "opacity-50 cursor-not-allowed pointer-events-none" : ""
         }`}
         onClick={() => setProjectionOpen(true)}
         disabled={!calculatedSummary}
       >
         Win Projections
       </button>
     </div>

     <div className="flex items-end">
       <button
         type="button"
         className={`w-full h-[42px] bg-red-600 dark:bg-red-700 text-white rounded-xl px-3 py-2 font-semibold hover:bg-red-700 hover:dark:bg-red-800 transition ${
           !calculatedSummary ? "opacity-50 cursor-not-allowed pointer-events-none" : ""
         }`}
         onClick={() => setLossProjectionOpen(true)}
         disabled={!calculatedSummary}
       >
         Loss Projections
       </button>
     </div>
   </div>
 </div>
  );

  const renderForexSettings = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="md:self-end">
        <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Pair<span className="text-red-500">*</span></label>
        <select 
          value={settings["Product"] || ""} 
          onChange={e => handleSettingChange("Product", e.target.value)} 
          className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 bg-gray-50 focus:ring-2 focus:ring-blue-400"
        >
          <option value="">Select {assetClass}</option>
          {favOpts.length > 0 && (
            <optgroup label="♥ Favorites">
              {favOpts.map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </optgroup>
          )}
          <optgroup label={`All ${assetClass}`}>
            {nonFavOpts.map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </optgroup>
        </select>
      </div>
      <div>
        <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Initial Balance</label>
        <input type="text" value={formatCurrency(settings["Initial Balance"])} disabled className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 bg-gray-50" />
      </div>
      <div>
        <div className="flex justify-between mb-1">
          <label className="block font-bold text-gray-800 dark:text-gray-200">Risk</label>
          <div className="flex text-xs border rounded overflow-hidden border-gray-200 dark:border-gray-600">
            <button className={`px-2 py-1 ${settings["Risk Mode"]==='%'?"bg-blue-600 text-white":"bg-gray-100 dark:bg-gray-700 text-gray-500"}`} onClick={()=>handleSettingChange("Risk Mode",'%')}>%</button>
            <button className={`px-2 py-1 ${settings["Risk Mode"]==='$'?"bg-blue-600 text-white":"bg-gray-100 dark:bg-gray-700 text-gray-500"}`} onClick={()=>handleSettingChange("Risk Mode",'$')}>$</button>
          </div>
        </div>
        <input type="number" value={settings["Risk Mode"]==='$' ? settings["Risk Dollar Amount"] : settings["Risk Percentage"]} onChange={e=>handleSettingChange(settings["Risk Mode"]==='$'?"Risk Dollar Amount":"Risk Percentage", e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400" />
      </div>
      
      <div>
        <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Stop Loss (Pips)</label>
        <input type="number" value={settings["Stop Loss"]||""} onChange={e=>handleSettingChange("Stop Loss",e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400"/>
      </div>
      <div>
        <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Take Profit (Pips)</label>
        <input type="number" value={settings["Take Profit"]||""} onChange={e=>handleSettingChange("Take Profit",e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400"/>
      </div>
      <div>
        <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Account Goal</label>
        <input type="number" value={settings["Account Goal"]||""} onChange={e=>handleSettingChange("Account Goal",e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-green-400"/>
      </div>

      <div>
        <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Running Balance</label>
        <input 
          type="number" 
          /* CHANGE THIS LINE: Use ?? instead of || */
          value={settings["Running Balance"] ?? ""} 
          onChange={e=>handleSettingChange("Running Balance",e.target.value)} 
          className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-green-400"
        />
      </div>
        <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200"># Trades to Goal</label>
            <input
              type="number"
              value={calculatedSummary && winProjection ? winProjection.tradeCount : ""}
              readOnly
              disabled
              className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-500 dark:text-white"
            />
          </div>

          <div className="flex items-end">
            <button
              type="button"
              className={`w-full h-[42px] bg-blue-600 dark:bg-blue-700 text-white rounded-xl px-3 py-2 font-semibold hover:bg-blue-700 hover:dark:bg-blue-800 transition ${
                !calculatedSummary ? "opacity-50 cursor-not-allowed pointer-events-none" : ""
              }`}
              onClick={() => setProjectionOpen(true)}
              disabled={!calculatedSummary}
            >
              Win Projections
            </button>
          </div>

          <div className="flex items-end">
            <button
              type="button"
              className={`w-full h-[42px] bg-red-600 dark:bg-red-700 text-white rounded-xl px-3 py-2 font-semibold hover:bg-red-700 hover:dark:bg-red-800 transition ${
                !calculatedSummary ? "opacity-50 cursor-not-allowed pointer-events-none" : ""
              }`}
              onClick={() => setLossProjectionOpen(true)}
              disabled={!calculatedSummary}
            >
              Loss Projections
            </button>
          </div>
        </div>
      </div>
  );

  return (
    <div className="space-y-8 max-w-5xl mx-auto py-10">
      
      <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl border border-gray-100 dark:border-gray-700 p-8 animate-fadeIn">
        <div className="flex flex-col md:flex-row justify-between mb-6 gap-4">
          <div className="flex items-center gap-2">
            <span className="bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-300 rounded-full p-2 shadow-sm"><FaChartLine size={20} /></span>
            <h2 className="text-2xl font-extrabold text-gray-900 dark:text-gray-100">Account Settings (Backtesting)</h2>
          </div>
         <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            {/* Filter ASSET_CLASSES based on isAdmin */}
            {ASSET_CLASSES.filter(ac => isAdmin || (ac !== "Stocks" && ac !== "Options")).map(ac => (
              <button
                key={ac}
                onClick={() => handleAssetClassChange(ac)}
                className={`px-4 py-2 rounded-md text-sm font-bold transition-all ${assetClass === ac ? "bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-300 shadow-sm" : "text-gray-500 dark:text-gray-400 hover:text-gray-700"}`}
              >
                {ac}
              </button>
            ))}
          </div>
        </div>

        {assetClass === "Futures" && renderFuturesSettings()}
        {assetClass === "Forex" && renderForexSettings()}
        {assetClass === "Stocks" && renderStocksSettings()}

        <div className="col-span-3 border-t border-gray-200 dark:border-gray-700 my-8" />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
          <div>
            <div className="flex justify-between mb-1 text-sm font-bold text-gray-800 dark:text-gray-200">
              <span>Daily Goal</span>
              <span>{formatCurrency(dailyGoalProg.done)} / {formatCurrency(dailyGoalProg.target)}</span>
            </div>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden">
              <div className="h-full bg-green-500 transition-all duration-500" style={{ width: `${dailyGoalProg.pct}%` }} />
            </div>
          </div>
          <div>
            <div className="flex justify-between mb-1 text-sm font-bold text-gray-800 dark:text-gray-200">
              <span>Daily Loss Limit</span>
              <span>{formatCurrency(dailyLossProg.lost)} / {formatCurrency(dailyLossProg.limit)}</span>
            </div>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden">
              <div className="h-full bg-red-500 transition-all duration-500" style={{ width: `${dailyLossProg.pct}%` }} />
            </div>
          </div>
        </div>
        
        <div className="mt-6">
            <div className="flex justify-between mb-1 text-sm font-bold text-gray-800 dark:text-gray-200">
              <span>Account Goal Progress</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden">
                <div className="h-full bg-blue-500 transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
        </div>

        <div className="col-span-3 border-t border-gray-200 dark:border-gray-700 my-8" />

        <div className="max-w-lg mx-auto border rounded-xl bg-gray-50 dark:bg-gray-800 p-4">
          <h3 className="text-lg font-bold mb-2 flex items-center dark:text-white gap-2"><IconGoal />Summary</h3>
          {renderSummaryContent()}
        </div>

        <div className="flex justify-end mt-6">
          <button 
            onClick={() => handleCalculate(true)} 
            disabled={savingSettings || !settings["Product"]}
            className="bg-gradient-to-r from-blue-600 to-blue-400 text-white font-bold px-6 py-3 rounded-xl shadow hover:from-blue-700 hover:to-blue-500 disabled:opacity-50"
          >
            {savingSettings ? "Calculating..." : "Calculate"}
          </button>
        </div>
      </div>


{!settings["Calculator Only"] && (
      <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl border border-gray-100 dark:border-gray-700 p-8 animate-fadeIn">
        <div className="flex items-center mb-6 border-b border-gray-100 dark:border-gray-700 pb-4">
          <span className="bg-yellow-100 dark:bg-yellow-900 text-yellow-600 dark:text-yellow-300 rounded-full p-2 mr-2 shadow-sm">
            <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
          </span>
          <div>
            <h3 className="text-xl font-bold text-yellow-700 dark:text-yellow-400">Journal Entry</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Log your trade details below. <span className="text-red-500">* denotes required</span></p>
          </div>
        </div>

        <div className="space-y-8">
          
          <div>
            <h4 className="text-sm uppercase tracking-wide text-gray-500 dark:text-gray-400 font-bold mb-4 border-l-4 border-blue-500 pl-3">1. Setup & Context</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Date<span className="text-red-500">*</span></label>
                <input type="date" value={journal.Date} onChange={e=>handleJournalChange("Date",e.target.value)} className="w-full px-4 py-2 h-[42px] rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400" />
              </div>

              {!settings["Hide Session"] && (
              <div>
                <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Session</label>
                <select id="session" value={journal.Session} onChange={e=>handleJournalChange("Session",e.target.value)} className="w-full px-4 py-2 h-[42px] rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400">
                  <option value="">Select</option>
                  {SESSION_OPTIONS.map(s=><option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              )}

              <div>
                <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Timeframe</label>
                <input id="timeframe" list="tf-options" value={journal.Timeframe} onChange={e=>handleJournalChange("Timeframe",e.target.value)} className="w-full px-4 py-2 h-[42px] rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400"/>
                <datalist id="tf-options">{TIMEFRAME_OPTIONS.map(t=><option key={t} value={t}/>)}</datalist>
              </div>
              <div>
                <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Position</label>
                <select id="position" value={journal.Position} onChange={e=>handleJournalChange("Position",e.target.value)} className="w-full px-4 py-2 h-[42px] rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400">
                  <option value="">Select</option>
                  {POSITION_OPTIONS.map(s=><option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-sm uppercase tracking-wide text-gray-500 dark:text-gray-400 font-bold mb-4 border-l-4 border-purple-500 pl-3">2. Execution</h4>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              
              <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
                <label className="block text-xs font-bold text-gray-400 dark:text-gray-300 uppercase mb-3">Entry Details</label>
                <div className="space-y-4">

                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Time</label>
                    <div className="relative flex items-center">
                      <input id="entryTime" type="time" ref={entryTimeRef} value={to24HourTime(journal["Entry Time"])} onChange={e=>setJournal(prev=>({...prev,"Entry Time":toAmPm(e.target.value)}))} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400" />
                      <FaRegClock className="absolute right-3 text-lg text-gray-400 cursor-pointer hover:text-blue-500" onClick={()=>entryTimeRef.current?.showPicker && entryTimeRef.current.showPicker()} />
                    </div>
                  </div>
                  

                  {!settings["Hide Entry Price"] && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Price</label>
                    <input id="entryPrice" type="number" value={journal["Entry Price"]||""} onChange={e=>handleJournalChange("Entry Price",e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400"/>
                  </div>
                  )}

                  {!settings["Hide Entry Strategy"] && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Strategy</label>
                    <input id="entryStrategy" list="entry-strats" value={journal["Entry Strategy"]} onChange={e=>handleJournalChange("Entry Strategy",e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400"/>
                    <datalist id="entry-strats">{entryStrategies.map(s=><option key={s} value={s}/>)}</datalist>
                  </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
                <label className="block text-xs font-bold text-gray-400 dark:text-gray-300 uppercase mb-3">Exit Details</label>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Time</label>
                    <div className="relative flex items-center">
                      <input id="exitTime" type="time" ref={exitTimeRef} value={to24HourTime(journal["Exit Time"])} onChange={e=>setJournal(prev=>({...prev,"Exit Time":toAmPm(e.target.value)}))} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400" />
                      <FaRegClock className="absolute right-3 text-lg text-gray-400 cursor-pointer hover:text-blue-500" onClick={()=>exitTimeRef.current?.showPicker && exitTimeRef.current.showPicker()} />
                    </div>
                  </div>

                   {!settings["Hide Exit Price"] && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Price</label>
                    <input id="exitPrice" type="number" value={journal["Exit Price"]||""} onChange={e=>handleJournalChange("Exit Price",e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400"/>
                  </div>
                  )}

                   {!settings["Hide Exit Strategy"] && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Strategy</label>
                    <input id="exitStrategy" list="exit-strats" value={journal["Exit Strategy"]} onChange={e=>handleJournalChange("Exit Strategy",e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400"/>
                    <datalist id="exit-strats">{exitStrategies.map(s=><option key={s} value={s}/>)}</datalist>
                  </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col justify-center bg-blue-50 dark:bg-blue-900/10 p-4 rounded-xl border border-blue-100 dark:border-blue-800">
                <label className="block text-xs font-bold text-gray-400 dark:text-gray-300 uppercase mb-3">Quantity</label>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-bold mb-1 text-gray-800 dark:text-gray-200">
                      Actual {getUnitLabel(assetClass)}<span className="text-red-500">*</span>
                    </label>
                    <input 
                      id="contracts" 
                      type="number" 
                      value={journal.Size||""} 
                      onChange={(e) => {
                        const val = e.target.value;
                        
                        setJournal(prev => {
                          // 1. Update the Size in state
                          const next = { ...prev, Size: val };
                          
                          // 2. Auto-Recalculate Balance if Ticks are present
                          const ticksVal = Number(next["Ticks"]);
                          if (!isNaN(ticksVal) && next["Ticks"] !== "" && next["Ticks"] != null) {
                             const prevBal = Number(settings["Running Balance"] || settings["Initial Balance"] || 0);
                             
                             // Use the NEW size we just typed (val)
                             const rawSize = val; 
                             const calcSize = calculatedSummary?.size || 0;
                             const size = (rawSize !== "" && rawSize != null) ? Number(rawSize) : calcSize;
                             
                             const mult = calculatedSummary?.multiplier || getMultiplier(assetClass, settings["Product"], settings);
                             
                             const amount = ticksVal * mult * size;
                             const isLoss = next["Loss"] === true;
                             
                             // Update Balance based on Win/Loss status
                             next["Actual Balance"] = (isLoss ? prevBal - amount : prevBal + amount).toFixed(2);
                          }
                          return next;
                        });
                      }} 
                      className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 font-mono" 
                      placeholder={calculatedSummary?.size||"0"} 
                    />
                  </div>
                    {/* NEW: Potential Ticks/Pips/Points Field */}
                                      {!settings["Hide Potential"] && (
                                        <div>
                                          <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200 flex items-center gap-2">
                                            Potential {assetClass === "Forex" ? "Pips" : assetClass === "Stocks" ? "Points" : "Ticks"}
                                            <Tooltip
                                              variant="blue"
                                              backdrop
                                              content={
                                                <div className="space-y-2">
                                                  <div className="text-sm uppercase tracking-wide font-bold opacity-100 text-blue-700 dark:text-blue-400">
                                                    Potential {assetClass === "Forex" ? "Pips" : assetClass === "Stocks" ? "Points" : "Ticks"}
                                                  </div>
                                                  <p className="text-[14px] leading-relaxed">
                                                    The total number of {assetClass === "Forex" ? "pips" : assetClass === "Stocks" ? "points" : "ticks"} that were available in the move.
                                                  </p>
                                                </div>
                                              }
                                            >
                                              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-[12px] font-bold cursor-help">
                                                ?
                                              </span>
                                            </Tooltip>
                                          </label>
                                          <input
                                            id="potentialTicks"
                                            type="number"
                                            value={journal["Potential"] ?? ""}
                                            onChange={(e) => handleJournalChange("Potential", e.target.value)}
                                            className="no-spin w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition font-mono"
                                            placeholder="Qty"
                                          />
                                        </div>
                                      )}
                                      </div>


                              {!settings["Hide Ideal Stop Loss"] && (
                              <div>
                                <label className="block font-bold mb-1 mt-4 text-gray-800 dark:text-gray-200 flex items-center gap-2">
                                  Ideal Stop Loss
                                  <Tooltip
                                    variant="blue"
                                    backdrop
                                    content={
                                      <div className="space-y-2">
                                        <div className="text-sm uppercase tracking-wide font-bold opacity-100 text-blue-700 dark:text-blue-400">
                                          Ideal Stop Loss
                                        </div>
                                        <p className="text-[14px] leading-relaxed">
                                          You should always use your strategy for setting the proper stop loss. You may notice that the stop loss is too big or too small.
                                        </p>
                                        <p className="text-[14px] leading-relaxed">
                                          You can use this field to help track a more ideal stop loss for the trade and see the results on the Stats page.
                                        </p>
                                      </div>
                                    }
                                  >
                                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-[12px] font-bold cursor-help">
                                      ?
                                    </span>
                                  </Tooltip>
                                </label>
                                <input
                                  id="ideal"
                                  type="number"
                                  value={journal["IdealSL"] ?? ""}
                                  onChange={e => handleJournalChange("IdealSL", e.target.value)}
                                  className="no-spin w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition font-mono"
                                  placeholder="Qty"
                                />
                              </div>
                              )}
                           </div>

                        </div>
                      </div>

          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
            
            <div className="flex items-center mb-6">
                <div className="h-6 w-1 bg-green-500 rounded-full mr-3"></div>
                <h4 className="text-sm uppercase tracking-wide text-gray-500 dark:text-gray-300 font-bold">3. Outcome</h4>
            </div>

            {/* 3a. Win/Loss Buttons */}
            <div className="mb-6">
              <label className="block font-bold mb-2 text-gray-800 dark:text-gray-200">
                Result{!isDidNotTake && <span className="text-red-500">*</span>}
              </label>
              <div className="flex gap-4 max-w-md">
                <button
                  type="button"
                  onClick={() => {
                    setJournal(prev => {
                      const next = { ...prev, Win: true, Loss: false };
                      
                      // RECALCULATE BALANCE AS A WIN
                      const ticksVal = Number(prev["Ticks"]);
                      if (!isNaN(ticksVal) && prev["Ticks"] !== "" && prev["Ticks"] != null) {
                         const prevBal = Number(settings["Running Balance"] || settings["Initial Balance"] || 0);
                         const rawSize = prev["Size"];
                         const calcSize = calculatedSummary?.size || 0;
                         const size = (rawSize !== "" && rawSize != null) ? Number(rawSize) : calcSize;
                         const mult = calculatedSummary?.multiplier || getMultiplier(assetClass, settings["Product"], settings);
                         
                         const amount = ticksVal * mult * size;
                         next["Actual Balance"] = (prevBal + amount).toFixed(2);
                      }
                      return next;
                    });
                  }}
                  disabled={isDidNotTake}
                  className={`flex-1 py-3 rounded-xl font-bold transition shadow-sm border ${
                    journal["Win"] 
                      ? "bg-green-500 text-white border-green-600 ring-2 ring-green-300 dark:ring-green-800" 
                      : "bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-green-50 dark:hover:bg-gray-600 disabled:opacity-50"
                  }`}
                >
                  WON
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setJournal(prev => {
                      const next = { ...prev, Win: false, Loss: true };
                      
                      // RECALCULATE BALANCE AS A LOSS
                      const ticksVal = Number(prev["Ticks"]);
                      if (!isNaN(ticksVal) && prev["Ticks"] !== "" && prev["Ticks"] != null) {
                         const prevBal = Number(settings["Running Balance"] || settings["Initial Balance"] || 0);
                         const rawSize = prev["Size"];
                         const calcSize = calculatedSummary?.size || 0;
                         const size = (rawSize !== "" && rawSize != null) ? Number(rawSize) : calcSize;
                         const mult = calculatedSummary?.multiplier || getMultiplier(assetClass, settings["Product"], settings);
                         
                         const amount = ticksVal * mult * size;
                         // SUBTRACT AMOUNT
                         next["Actual Balance"] = (prevBal - amount).toFixed(2);
                      }
                      return next;
                    });
                  }}
                  disabled={isDidNotTake}
                  className={`flex-1 py-3 rounded-xl font-bold transition shadow-sm border ${
                    journal["Loss"] 
                      ? "bg-red-500 text-white border-red-600 ring-2 ring-red-300 dark:ring-red-900" 
                      : "bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-red-50 dark:hover:bg-gray-600 disabled:opacity-50"
                  }`}
                >
                  LOSS
                </button>
              </div>
            
              
              {/* Instruction Box */}
              <div className="border border-orange-400/50 rounded-xl p-4 bg-transparent mt-6 mb-4">
                <div className="flex items-start gap-3">
                  <span className="text-yellow-400 text-lg mt-0.5">💡</span>
                  <div className="text-sm">
                    <p className="mb-1 text-yellow-800 dark:text-yellow-300">
                      Enter <strong className="text-yellow-400">New Balance</strong> OR <strong className="text-yellow-400"># of {assetClass === "Forex" ? "Pips" : assetClass === "Stocks" ? "Points" : "Ticks"}</strong>.
                    </p>
                    <p className="text-yellow-800 dark:text-yellow-300 leading-relaxed">
                      Entering {assetClass === "Forex" ? "Pips" : assetClass === "Stocks" ? "Points" : "Ticks"} will auto-calculate the New Balance based on your lot size. 
                      If you enter a New Balance, the {assetClass === "Forex" ? "Pips" : assetClass === "Stocks" ? "Points" : "Ticks"} field will clear.
                    </p>
                  </div>
                </div>
              </div>

              {/* Inputs */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                
                {/* New Balance */}
                <div>
                   <label className="block font-bold mb-2 text-gray-300 text-sm">New Account Balance</label>
                   <div className="relative">
                     <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-bold">$</span>
                     <input
                        id="actual-balance-input"
                        type="text"
                        inputMode="decimal"
                        value={journal["Actual Balance"] ?? ""}
                        onChange={(e) => {
                          const raw = e.target.value;
                          setJournal((j) => ({
                            ...j,
                            ["Actual Balance"]: raw,
                            ["Ticks"]: raw ? "" : j["Ticks"],
                          }));
                        }}
                         onBlur={(e) => {
                           const raw = String(e.target.value ?? "").trim();
                           setJournal((j) => ({
                             ...j,
                             ["Actual Balance"]: (raw === "" || String(j["Ticks"] ?? "").trim() !== "")
                               ? ""
                               : parseCurrency(raw),
                           }));
                         }}
                        className={`w-full pl-8 px-4 py-3 rounded-xl border bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-white transition font-bold text-lg shadow-inner focus:ring-2 focus:ring-blue-500 ${
                          missingFields.includes("- Actual Balance or Ticks")
                            ? "border-red-500"
                            : "border-gray-200 dark:border-gray-600"
                        }`}
                        placeholder="0.00"
                      />
                   </div>
                </div>

                {/* Ticks */}
                <div>
                  <label className="block font-bold mb-2 text-gray-300 text-sm">
                     {assetClass === "Forex" ? "Pips" : assetClass === "Stocks" ? "Points" : "Ticks"} Captured/Lost
                  </label>
                  <input
                    id="ticks"
                    type="number"
                    value={journal["Ticks"] || ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      const ticks = Number(val);
                      
                      if (!isNaN(ticks) && val !== "") {
                          const prevBal = Number(settings["Running Balance"] || settings["Initial Balance"] || 0);
                          
                          // Get Size (Manual Override > Calculated)
                          const rawSize = journal["Size"];
                          const calcSize = calculatedSummary?.size || 0;
                          const size = (rawSize !== "" && rawSize !== null && rawSize !== undefined) ? Number(rawSize) : calcSize;
                          
                          // Get Multiplier (Point Value)
                          const mult = calculatedSummary?.multiplier || getMultiplier(assetClass, settings["Product"], settings);

                          // Calculate Profit/Loss Amount
                          const amount = ticks * mult * size;
                          
                          // Subtract if Loss is selected, otherwise Add
                          const isLoss = journal["Loss"] === true;
                          const newBal = isLoss ? (prevBal - amount) : (prevBal + amount);

                          setJournal((j) => ({
                            ...j,
                            ["Ticks"]: val,
                            ["Actual Balance"]: newBal.toFixed(2)
                          }));
                      } else {
                           setJournal((j) => ({ ...j, ["Ticks"]: val }));
                      }
                    }}
                    className={`w-full px-4 py-3 rounded-xl border bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-white transition font-bold text-lg shadow-inner focus:ring-2 focus:ring-blue-500 ${
                      missingFields.includes("- Actual Balance or Ticks")
                        ? "border-red-500"
                        : "border-gray-200 dark:border-gray-600"
                    }`}
                    placeholder="Qty"
                    inputMode="numeric"
                  />
                </div>
              </div>
            </div>
          </div>



          {/* SECTION 4: PSYCHOLOGY & REVIEW */}
          <div>
            <h4 className="text-sm uppercase tracking-wide text-gray-500 dark:text-gray-400 font-bold mb-4 border-l-4 border-yellow-500 pl-3">
              4. Psychology & Review
            </h4>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
              {/* Rules */}
              <div>
                  <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Followed Rules?</label>
                  <select
                    id="rules"
                    value={journal["Did I follow my Rules?"]}
                    onChange={e => handleJournalChange("Did I follow my Rules?", e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition"
                  >
                    <option value="">Select</option>
                    {RULES_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
              </div>
              {/* Mood */}
              {!settings["Hide Mood"] && (
              <div>
                  <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Mood (1-5)</label>
                  <select
                    id="mood"
                    value={journal["Mood_Mindset"]}
                    onChange={e => handleJournalChange("Mood_Mindset", e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition"
                  >
                    <option value="">Select (1=Calm, 5=Stress)</option>
                    {MOOD_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
              </div>
              )}

              {/* Grade */}
              {!settings["Hide Grade"] && (
              <div>
                  <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Grade</label>
                  <select
                    id="grade"
                    value={journal["Grade"]}
                    onChange={e => handleJournalChange("Grade", e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition"
                  >
                    <option value="">Select</option>
                    {GRADE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
              </div>
              )}
            </div>

            {/* Inputs */}
            <div className="space-y-6">
               <div>
                 <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Link / Screenshot</label>
                 <input
                    id="link"
                    type="text"
                    value={journal["Links_Screenshots"]}
                    onChange={e => handleJournalChange("Links_Screenshots", e.target.value)}
                    placeholder="Paste URL here..."
                    className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition"
                  />
               </div>
               
               <div>
                 <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Notes</label>
                 <textarea
                    id="notes"
                    rows={3}
                    value={journal["Notes"]}
                    onChange={e => handleJournalChange("Notes", e.target.value)}
                    placeholder="Anything to note about this trade? How were you feeling physically?"
                    className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition resize-y"
                  />
               </div>

               {!settings["Hide Lesson"] && (
               <div className="bg-blue-50 dark:bg-blue-900/20 p-5 rounded-xl border border-blue-100 dark:border-blue-800/50">
                  <label className="block font-bold mb-2 text-blue-800 dark:text-blue-300">
                    Lesson (Optional)
                  </label>
                  <textarea
                    id="lesson"
                    rows={2}
                    value={journal["Lesson"]}
                    onChange={e => handleJournalChange("Lesson", e.target.value)}
                    placeholder="What did you learn from this trade?"
                    className="w-full px-4 py-2 rounded-lg border border-blue-200 dark:border-blue-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-800 dark:text-white bg-white transition resize-y"
                  />
                  <div className="mt-3">
                    <label className="inline-flex items-center text-blue-800 dark:text-blue-300 font-semibold cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={journal["LogLesson"]}
                        onChange={e => handleJournalChange("LogLesson", e.target.checked)}
                        className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      Save to Lessons Database
                    </label>
                  </div>
               </div>
               )}
            </div>
          </div>
        
        </div>

        <div className="flex justify-end mt-10 pt-6 border-t border-gray-100 dark:border-gray-700">
          <button 
            onClick={handleSaveTrade} 
            disabled={savingTrade}
            className="w-full sm:w-auto min-w-[200px] bg-gradient-to-r from-orange-500 to-orange-400 dark:from-orange-700 dark:to-orange-500 text-white font-bold px-8 py-3 rounded-xl shadow-lg hover:from-orange-600 hover:to-orange-500 transform hover:-translate-y-0.5 transition-all disabled:opacity-50"
          >
            {savingTrade ? "Saving..." : "Save Trade"}
          </button>
        </div>
      </div>
      )} {/* <--- 2. ADD THIS CLOSING BRACE/PARENTHESIS HERE */}

      {/* POPUP MODALS */}
{currentPopup?.id === 'tradeSaved' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-20 animate-fadeIn">
    <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl px-10 py-8 flex flex-col items-center border-2 border-blue-500 animate-fadeIn">
      <span className="flex items-center justify-center bg-blue-100 dark:bg-blue-800 rounded-full mb-3" style={{ width: 60, height: 60 }}>
        <svg className="w-10 h-10 text-blue-600 dark:text-blue-300" fill="none" viewBox="0 0 48 48">
          <circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="4" fill="currentColor" opacity="0.13"/>
          <path d="M16 24l6 6 10-14" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
        </svg>
      </span>
      <div className="text-2xl font-extrabold text-blue-700 dark:text-blue-300 mb-1">
        Trade Saved!
      </div>
      <div className="text-gray-700 dark:text-gray-200 mb-1 text-center">
        Your journal entry has been saved.
      </div>
      <button
        className="mt-2 px-5 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold shadow transition"
        onClick={closeCurrentPopup}
      >
        OK
      </button>
    </div>
  </div>
)}

{/* REQUIRED FIELDS */}
{missingFields.length > 0 && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 animate-fadeIn">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={() => setMissingFields([])}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
        </svg>
        Required Fields Missing
      </h2>
      <ul className="mb-4 text-gray-800 dark:text-gray-200">
        {missingFields.map((f) => (
   <li key={f} className="pl-2">{f}</li>
 ))}

      </ul>
      <button
        onClick={() => setMissingFields([])}
        className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold px-4 py-2 rounded shadow"
      >
        Close
      </button>
    </div>
  </div>
)}

      {/* WIN TABLE */}
      {projectionOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center animate-fadeIn">
          <div className="bg-gradient-to-br from-blue-50/90 via-white/90 to-blue-100/80 dark:from-[#1e293b] dark:via-gray-800 dark:to-[#1e293b]
            max-w-4xl w-full rounded-2xl shadow-2xl border-4 border-blue-300 dark:border-blue-900 p-8 relative overflow-auto max-h-[85vh]">
            
            <button
              className="absolute top-2 right-2 bg-gray-200 dark:bg-gray-900 text-gray-600 dark:text-gray-200 rounded-full w-8 h-8 flex items-center justify-center hover:bg-blue-100 hover:dark:bg-blue-800 transition"
              onClick={() => setProjectionOpen(false)}
              aria-label="Close"
            >×</button>
              
            <h3 className="text-2xl font-bold mb-4 text-blue-700 dark:text-blue-300 flex items-center gap-2">
              <svg width="28" height="28" fill="none" viewBox="0 0 24 24" className="mr-2">
                <circle cx="12" cy="12" r="10" stroke="#2563eb" strokeWidth="2" />
                <path d="M8 12l2 2l4-4" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" />
              </svg>
              Win Projection Table
            </h3>

            {/* Explanatory Text Matching Originals */}
            <p className="mb-6 text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
              Projections show how many trades {assetClass === "Stocks" ? "" : "(using at least your minimum size setting) "} 
              it would take to reach your goal. Any trades with the red background indicate when you are over your risk tolerance. 
              The totals are estimates and don't take into account broker fees and commissions.
              {assetClass !== "Stocks" && (
                <>
                  <br /><br />
                  If you prefer to always trade the same {getUnitLabel(assetClass).toLowerCase()}, you can set the minimum and maximum 
                  {assetClass === "Forex" ? " lot size" : " contracts"} to the same number in the settings.
                </>
              )}
            </p>
            
            {/* Dynamic Time Estimation Block */}
            {(() => {
              const accountGoal = Number(settings["Account Goal"] || 0);
              const running = Number(settings["Running Balance"] || 0);
              const dailyGoal = Number(settings["Daily Goal"] || 0);
              const tradeCount = winProjection?.tradeCount || 0;

              if (accountGoal <= 0) return null;
              if (running >= accountGoal) {
                return (
                  <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 text-sm font-bold text-green-800 dark:text-green-200">
                    You’re already at or above your account goal of {formatCurrency(accountGoal)}.
                  </div>
                );
              }

              // Calculate "Trading Days"
              let estimatedDays = 0;
              let currentDayAccumulator = 0;
              const trades = winProjection?.trades || [];

              if (dailyGoal > 0) {
                for (const t of trades) {
                  const profit = t.tradeProfit || 0;
                  if (profit >= dailyGoal) {
                    estimatedDays++;
                    currentDayAccumulator = 0;
                  } else {
                    currentDayAccumulator += profit;
                    if (currentDayAccumulator >= dailyGoal) {
                      estimatedDays++;
                      currentDayAccumulator = 0;
                    }
                  }
                }
                if (currentDayAccumulator > 0) estimatedDays++;
              } else {
                estimatedDays = tradeCount;
              }

              return (
                <div className="mb-6 p-5 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800/50">
                  <h4 className="font-bold text-blue-900 dark:text-blue-100 mb-3 flex items-center gap-2">
                    <FaRegClock className="text-blue-500" />
                    Estimated Time to Goal
                  </h4>
                  
                  <div className="flex flex-col sm:flex-row gap-6 items-start">
                    <div className="flex-1">
                      <div className="text-3xl font-extrabold text-blue-700 dark:text-blue-300">
                        {estimatedDays} <span className="text-lg font-medium text-gray-600 dark:text-gray-400">trading days</span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 leading-relaxed">
                        To reach <strong>{formatCurrency(accountGoal)}</strong> requires approximately <strong>{tradeCount}</strong> winning trades.
                      </p>
                    </div>

                    <div className="flex-1 text-sm text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm">
                      <strong className="block text-gray-800 dark:text-gray-200 mb-1">How is this calculated?</strong>
                      <ul className="list-disc pl-4 space-y-1">
                        <li>
                          While trade profit &lt; <strong>{formatCurrency(dailyGoal)}</strong>, we assume you trade until you hit your daily goal.
                        </li>
                        <li>
                          Once trade profit &ge; <strong>{formatCurrency(dailyGoal)}</strong>, we assume 1 trade per day.
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Projection Table */}
            {winProjection?.tradeCount === 0 && (
              <div className="text-center text-green-600 dark:text-green-300 py-4 font-semibold">
               No trades can be taken with the current settings. If you have reached your goal you can adjust it and continue trading.
              </div>
            )}

            {winProjection?.tradeCount > 0 && (
              <div className="overflow-auto max-h-[50vh] rounded-xl border-2 border-blue-200 dark:border-blue-800 shadow">
                <table className="min-w-full border-separate border-spacing-0 bg-white dark:bg-gray-900">
                  <thead className="bg-blue-100 dark:bg-blue-900/80 sticky top-0 z-10">
                    <tr>
                      <th className="px-3 py-2 border-b-2 border-blue-200 dark:border-blue-800 text-left text-blue-900 dark:text-blue-200 font-semibold">Trade #</th>
                      <th className="px-3 py-2 border-b-2 border-blue-200 dark:border-blue-800 text-left text-blue-900 dark:text-blue-200 font-semibold">Balance</th>
                      <th className="px-3 py-2 border-b-2 border-blue-200 dark:border-blue-800 text-left text-blue-900 dark:text-blue-200 font-semibold">{assetClass === "Stocks" ? "Investment" : "Max Drawdown"}</th>
                      <th className="px-3 py-2 border-b-2 border-blue-200 dark:border-blue-800 text-left text-blue-900 dark:text-blue-200 font-semibold">{getUnitLabel(assetClass)}</th>
                      <th className="px-3 py-2 border-b-2 border-blue-200 dark:border-blue-800 text-left text-blue-900 dark:text-blue-200 font-semibold">Total Risk</th>
                      <th className="px-3 py-2 border-b-2 border-blue-200 dark:border-blue-800 text-left text-blue-900 dark:text-blue-200 font-semibold">Total Profit</th>
                      <th className="px-3 py-2 border-b-2 border-blue-200 dark:border-blue-800 text-left text-blue-900 dark:text-blue-200 font-semibold">End Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      let runningBal = Number(settings["Running Balance"] || 0);
                      // --- NEW: Slice data if trialing ---
                                    const rowsToShow = isTrialing 
                                      ? winProjection.trades.slice(0, 10) 
                                      : winProjection.trades;

                      return rowsToShow.map((row, i) => {
                        const before = runningBal;
                        const after  = before + (row.tradeProfit ?? 0);
                        runningBal = after;

                        return (
                          <tr
                            key={i}
                            className={`${
                              row.underfunded 
                                ? "bg-red-100 dark:bg-red-900/80" 
                                : "hover:bg-blue-50 dark:hover:bg-blue-900/30 bg-white dark:bg-gray-900"
                            } transition border-b border-gray-100 dark:border-gray-800`}
                          >
                            <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{row.trade}</td>
                            <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{formatCurrency(before)}</td>
                            <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{formatCurrency(row.riskDollar)}</td>
                            <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{row.size || row.contracts || row.lots}</td>
                            <td className={`px-3 py-2 ${row.size === 0 ? "text-red-600 font-bold" : "text-gray-900 dark:text-gray-100"}`}>
                              {formatCurrency(row.totalRisk)}
                            </td>
                            <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{formatCurrency(row.tradeProfit)}</td>
                            <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{formatCurrency(after)}</td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
      {/* --- NEW: Trial Indicator --- */}
              {isTrialing && winProjection.trades.length > 5 && (
                <div className="p-4 bg-gray-100 dark:bg-gray-800 border-t-2 border-gray-200 dark:border-gray-700 text-center">
                  <p className="text-lg font-bold text-gray-600 dark:text-red-500 flex items-center justify-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /></svg>
                    Full projection table available after Free Trial
                  </p>
                </div>
              )}
            </div>
            )}
    </div>
  </div>
)}

      {/* --- LOSS PROJECTION MODAL --- */}
      {lossProjectionOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center animate-fadeIn">
          <div className="bg-gradient-to-br from-red-50/90 via-white/90 to-red-100/80 dark:from-[#2d1616] dark:via-gray-800 dark:to-[#2d1616]
            max-w-4xl w-full rounded-2xl shadow-2xl border-4 border-red-300 dark:border-red-900 p-8 relative overflow-auto max-h-[85vh]">
            
            <button
              className="absolute top-2 right-2 bg-gray-200 dark:bg-gray-900 text-gray-600 dark:text-gray-200 rounded-full w-8 h-8 flex items-center justify-center hover:bg-red-100 hover:dark:bg-red-800 transition"
              onClick={() => setLossProjectionOpen(false)}
              aria-label="Close"
            >×</button>

            <h3 className="text-2xl font-bold mb-4 text-red-700 dark:text-red-300 flex items-center gap-2">
              <svg width="28" height="28" fill="none" viewBox="0 0 24 24" className="mr-2">
                <circle cx="12" cy="12" r="10" stroke="#dc2626" strokeWidth="2" />
                <path d="M12 8v4l3 3" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" />
              </svg>
              Loss Projection Table
            </h3>

            <p className="mb-6 text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
              Projections show how many consecutive full-loss trades {assetClass === "Stocks" ? "" : "(using at least your minimum size setting) "}
              it would take to blow your account. The totals are estimates and do not include fees.
              {assetClass !== "Stocks" && (
                <>
                  <br /><br />
                  If you prefer to always trade the same {getUnitLabel(assetClass).toLowerCase()}, you can set the minimum and maximum 
                  {assetClass === "Forex" ? " lot size" : " contracts"} to the same number in the settings.
                </>
              )}
            </p>

            {/* Dynamic Risk Analysis Block */}
            {(() => {
              const dailyLoss = Number(settings["Daily Loss Limit"] || 0);
              const init = Number(settings["Initial Balance"] || 0);
              const maxLoss = Number(settings["Maximum Loss Limit"] || settings["Max Loss Limit"] || 0);
              const running = Number(settings["Running Balance"] || 0);
              const tradeCount = lossProjection?.tradeCount || 0;

              if (!(dailyLoss > 0)) return null;

             // Inside the Loss Projection Modal's dynamic block
             let isProp = (init > 0 && maxLoss > 0);
             let ruinThreshold = 0;

             if (isProp) {
               ruinThreshold = Math.max(0, init - maxLoss);
             } else {
               // Use $1.00 as the ruin threshold for retail stock accounts
               ruinThreshold = (assetClass === "Stocks") ? 1 : 0;
             }

             const distance = Math.max(0, running - ruinThreshold);
             const daysToRuin = dailyLoss > 0 ? Math.ceil(distance / dailyLoss) : 0;

              return (
                <div className="mb-6 p-5 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800/50">
                  <h4 className="font-bold text-red-900 dark:text-red-100 mb-3 flex items-center gap-2">
                    <FaExclamationTriangle className="text-red-500" />
                    Risk Analysis
                  </h4>
                  
                  <div className="flex flex-col sm:flex-row gap-6 items-start">
                    <div className="flex-1">
                      <div className="text-3xl font-extrabold text-red-700 dark:text-red-300">
                        {tradeCount} <span className="text-lg font-medium text-gray-600 dark:text-gray-400">consecutive losses</span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 leading-relaxed">
                        To reach your failure threshold of <strong>{formatCurrency(ruinThreshold)}</strong> based on your current risk settings.
                      </p>
                    </div>

                    <div className="flex-1 bg-white dark:bg-gray-800 p-3 rounded-lg border border-red-100 dark:border-red-900 shadow-sm">
                      <div className="text-xs text-gray-500 dark:text-gray-400 uppercase font-bold tracking-wider mb-1">Time to Ruin (Daily Limit)</div>
                      <div className="text-xl font-bold text-gray-800 dark:text-gray-100">
                        {daysToRuin} <span className="text-sm font-medium text-gray-500">trading days</span>
                      </div>
                      <div className="text-xs text-red-600 dark:text-red-400 font-medium mt-1">
                        If you hit your Daily Loss Limit ({formatCurrency(dailyLoss)}) every single day.
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            <div className="overflow-auto max-h-[50vh] rounded-xl border-2 border-red-200 dark:border-red-800 shadow">
              <table className="min-w-full border-separate border-spacing-0 bg-white dark:bg-gray-900">
                <thead className="bg-red-100 dark:bg-red-900/80 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 border-b-2 border-red-200 dark:border-red-800 text-left text-red-900 dark:text-red-200 font-semibold">Trade #</th>
                    <th className="px-4 py-3 border-b-2 border-red-200 dark:border-red-800 text-left text-red-900 dark:text-red-200 font-semibold">Balance Before</th>
                    <th className="px-4 py-3 border-b-2 border-red-200 dark:border-red-800 text-left text-red-900 dark:text-red-200 font-semibold">{getUnitLabel(assetClass)}</th>
                    <th className="px-4 py-3 border-b-2 border-red-200 dark:border-red-800 text-left text-red-900 dark:text-red-200 font-semibold">Loss Amount</th>
                    <th className="px-4 py-3 border-b-2 border-red-200 dark:border-red-800 text-left text-red-900 dark:text-red-200 font-semibold">Balance After</th>
                  </tr>
                </thead>
                <tbody>
                  {(lossProjection?.trades?.length ?? 0) === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400 italic">
                        No trades can be taken with the current settings.
                      </td>
                    </tr>
                  ) : (
                    (isTrialing ? lossProjection.trades.slice(0, 10) : lossProjection.trades).map((row, i) => (
                      <tr key={i} className="hover:bg-red-50 dark:hover:bg-red-900/40 transition group border-b border-gray-100 dark:border-gray-800">
                        <td className="px-4 py-2 text-gray-900 dark:text-gray-100">{row.trade}</td>
                        <td className="px-4 py-2 text-gray-900 dark:text-gray-100">
                          {formatCurrency(row.balanceBefore)}
                        </td>
                        <td className="px-4 py-2 text-gray-900 dark:text-gray-100">
                          {row.size || row.contracts || row.lots}
                        </td>
                        <td className="px-4 py-2 text-red-600 dark:text-red-400 font-bold group-hover:text-red-700">
                          {formatCurrency(row.loss)}
                        </td>
                        <td className="px-4 py-2 text-gray-900 dark:text-gray-100">
                          {formatCurrency(row.balanceAfter)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
             {/* --- NEW: Trial Indicator --- */}
        {isTrialing && (lossProjection?.trades?.length ?? 0) > 5 && (
          <div className="p-4 bg-gray-100 dark:bg-gray-800 border-t-2 border-gray-200 dark:border-gray-700 text-center">
            <p className="text-lg font-bold text-gray-600 dark:text-red-500 flex items-center justify-center gap-2">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /></svg>
              Full projection table available after Free Trial
            </p>
          </div>
        )}
      </div>
    </div>
  </div>
)}
 )}

{showZeroContractsPopup && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={() => setShowZeroContractsPopup(false)}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
        </svg>
        Warning!
      </h2>

      <p className="mb-3 text-gray-800 dark:text-gray-100">
        Based on the product you selected, your account balance, risk tolerance, and stop loss size, the suggested number of contracts for this trade is <span className="font-bold text-red-500">0</span>.
      </p>

      <p className="mb-3 text-gray-800 dark:text-gray-100">
        You are currently risking <span className="font-bold text-red-500">{lastActualRiskPercent}%</span> of your balance, which is <span className="font-bold text-red-500">{formatCurrency(lastTotalRisk)}</span>.
      </p>

      {/* Tip for Min Contracts */}
      {(() => {
        // Use the min contracts from the calculation, falling back to settings
        const minQty = Number(calculatedSummary?.minContracts || settings["Minimum Contracts"] || 1);

        // maxStopLossSize is for 1 contract; divide by minQty to get ticks allowed for minQty contracts
        const maxSLForMin = (Number(calculatedSummary?.maxStopLossSizeForMin) > 0 && minQty > 0)
          ? Math.floor(Number(calculatedSummary.maxStopLossSizeForMin) / minQty)
          : 0;

        if (maxSLForMin <= 0) return null; // don’t show if not meaningful

        return (
          <p className="mb-6 text-gray-800 dark:text-gray-100">
            Tip: To trade <span className="font-semibold">{minQty}</span> contract{minQty > 1 ? "s" : ""} within your risk, your stop loss must be ≤{" "}
            <span className="font-bold text-yellow-600">{maxSLForMin}</span> ticks.
          </p>
        );
      })()}

      <p className="mb-6 text-gray-700 dark:text-gray-300">
        Consider choosing a different product or waiting for a trade setup that requires a smaller stop loss.
      </p>

      <button
        onClick={() => setShowZeroContractsPopup(false)}
        className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}





{currentPopup?.id === 'dailyGoalPopup' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 animate-fadeIn">
    {/* Confetti for celebration! */}
    <Confetti width={width} height={height} numberOfPieces={500} recycle={false} />
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-green-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>

      <h2 className="text-2xl font-bold mb-4 text-green-700 dark:text-green-500 flex items-center">
        <svg className="w-7 h-7 mr-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" strokeWidth="2" />
          <path d="M9 12l2 2 4-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Congratulations!
      </h2>

      <div className="mb-4 text-lg text-gray-800 dark:text-gray-100 font-semibold text-center">
        You reached your{" "}
        <span className="text-green-600 dark:text-green-300 font-bold">Daily Goal</span>!
        <br />
        {currentPopup.data?.dailyGoal !== undefined && (
          <span className="block mt-2 text-base text-green-700 dark:text-green-500 font-bold">
            Goal:{" "}
            <span className="text-green-700 dark:text-green-500">
              {Number(currentPopup.data.dailyGoal).toLocaleString(undefined, {
                style: "currency",
                currency: "USD",
                minimumFractionDigits: 2,
              })}
            </span>
          </span>
        )}

        <span className="block mt-2 font-bold text-lg text-gray-700 dark:text-gray-200">
          Take a moment to celebrate your discipline and progress.<br /><br />
          Size down if you continue to trade, you don't want to risk giving your earnings back to the market.<br /><br />
          {getDailyGoalMessage()}
        </span>
      </div>

      <button
        onClick={closeCurrentPopup}
        className="bg-green-500 hover:bg-green-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}



{currentPopup?.id === 'overtradingPopup' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div
      className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative animate-fadeIn"
      role="dialog"
      aria-modal="true"
      aria-labelledby="overtrading-title"
      aria-describedby="overtrading-desc"
    >
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >
        ×
      </button>

      <h2
        id="overtrading-title"
        className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center"
      >
        <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
        </svg>
        Warning: Overtrading!
      </h2>

      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold text-yellow-700 dark:text-yellow-300">You have logged more trades than your daily plan allows.</span>
      </p>

      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold">Your maximum trades per day is: </span>
        <span className="font-bold text-yellow-700 dark:text-yellow-300">
          {currentPopup.data.maxTradesPerDay}
        </span>
      </p>

      <p id="overtrading-desc" className="mb-6 font-semibold text-gray-700 dark:text-gray-300">
        Overtrading can lead to impulsive decisions and unnecessary risk by draining your mental capital.
      </p>

      <p className="mt-4 mb-6 font-semibold text-gray-700 dark:text-gray-300">
        Review your trades, lock your account, and come back tomorrow recharged for a strong start!
      </p>

      <p className="mt-4 mb-6 font-semibold text-gray-700 dark:text-gray-300">
        Below is a really good video from Psychology of Profits regarding protecting your mental capital.
      </p>

      {/* Actions */}
      <div className="mt-6 flex flex-wrap gap-3 justify-end">
        <a
          href="https://www.youtube.com/watch?v=6vZG4epFEOM"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 border border-yellow-500 text-yellow-700 dark:text-yellow-300 font-bold px-4 py-2 rounded hover:bg-yellow-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition"
        >
          {/* Play icon */}
          <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M6 4l10 6-10 6V4z" />
          </svg>
          Watch: Mental Capital
          <span className="sr-only">(opens in a new tab)</span>
        </a>

        <button
          onClick={closeCurrentPopup}
          className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-6 py-2 rounded shadow transition"
        >
          Close
        </button>
      </div>
    </div>
  </div>

)}


{currentPopup?.id === 'maxLossLimitBlown' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-red-600 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
        <div className="flex items-center mb-4">

          <svg className="w-6 h-6 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
          </svg>

          <h2 className="text-xl font-bold text-red-600 dark:text-red-400">
            Account Blown
          </h2>
          
        </div>
      <p className="mb-4 font-semibold text-gray-800 dark:text-gray-100">
        Your balance has reached <span className="font-bold text-red-600">$0</span>. <br/><br/>This is a good time to switch to your demo account to review and refine your plan before depositing more funds.
      </p>
      <p className="font-bold mt-4 mb-4 text-gray-800 dark:text-gray-100">
        The key is to preserve your capital.
      </p>
        <p className="font-bold mt-4 mb-4 text-gray-800 dark:text-gray-100">
        Spend time backtesting until you've mastered your strategy and are consistently profitable. This is the best way to prepare before investing more of your hard-earned money.
      </p>

      {/* Add this just above the close button area in maxLossLimitBlown */}
      <p className="mt-4 mb-6 font-semibold text-gray-700 dark:text-gray-100">
        Trading is 80% Psychology.  Review mindset and risk-management refreshers, the Psychology of Profit has some really good videos.
      </p>

      {/* Actions */}
      <div className="mt-6 flex flex-wrap gap-3 justify-end">
        <a
          href="https://www.youtube.com/@Psychology-Of-Profit" 
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 border border-red-600 text-red-700 dark:text-red-300 font-bold px-4 py-2 rounded hover:bg-red-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-600 transition"
        >
          {/* Play icon */}
          <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M6 4l10 6-10 6V4z" />
          </svg>
          Visit the Psychology of Profit Youtube Channel
          <span className="sr-only">(opens in a new tab)</span>
        </a>

             </div>

      <p className="font-bold mt-4 mb-4 text-red-800 dark:text-red-400">
        Don't give up. You can do this!  
      </p>
      <button
        onClick={closeCurrentPopup}
        className="bg-red-600 hover:bg-red-700 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>

    </div>
  </div>
)}



{currentPopup?.id === 'tooManyLossesPopup' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-red-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-red-700 dark:text-red-500 flex items-center">
        <svg className="w-6 h-6 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
        </svg>
        Daily Lost Trades Limit Reached!
      </h2>
      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold text-red-700 dark:text-red-300">
          You've hit your max daily loss count.
        </span>
      </p>
      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold">Your maximum losses per day is: </span>
        <span className="font-bold text-red-700 dark:text-red-300">
          {currentPopup.data.maxLossesPerDay}
        </span>
      </p>
      <p className="mb-4 font-semibold text-gray-700 dark:text-gray-300">
         Consider stopping for the day to protect your capital. <br/> <br/>Review your trades and reset for tomorrow.
      </p>
      <button
        onClick={closeCurrentPopup}
        className="bg-red-500 hover:bg-red-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}


{currentPopup?.id === 'lossLimitWarning' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-red-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-red-700 dark:text-red-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" strokeWidth="2" />
          <path d="M12 8v4l3 3" strokeWidth="2" strokeLinecap="round" />
        </svg>
        Daily Loss Limit Exceeded!
      </h2>
      <p className="mb-3  font-semibold text-lg text-gray-800 dark:text-gray-100">
        Your total loss for today is <span className="font-bold text-red-600">{formatCurrency(currentPopup.data.lossForDay)}</span>,
        which exceeds your daily loss limit of <span className="font-bold text-red-600">{formatCurrency(currentPopup.data.lossLimitAmount)}</span>.
      </p>
      <p className="mb-6 font-semibold text-gray-700 dark:text-gray-400">
        Please consider stopping trading for the day to protect your account.
      </p>
      <p className="mb-6 font-semibold text-red-700 dark:text-red-400">
        You are worthy of success!  Don't give up!
      </p>
      <button
        onClick={closeCurrentPopup}
        className="bg-red-500 hover:bg-red-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}


{currentPopup?.id === "win10NoRulesPopup" && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
        </svg>
        Warning - Big Wins Without Following Rules
      </h2>
      <div className="font-bold mb-4 text-gray-800 dark:text-gray-100 whitespace-pre-line">
        {currentPopup.data?.message
          ?.split('\n')
          .map((line, i) =>
            line.trim() === "Discipline > excitement!" ? (
              <span key={i} className="font-bold text-blue-700 dark:text-blue-500">
                Discipline &gt; excitement!
                <br />
              </span>
            ) : (
              <React.Fragment key={i}>
                {renderWithTiltTooltip(line)}
                <br />
              </React.Fragment>
            )
          )
        }
      </div>
      <button
        onClick={closeCurrentPopup}
        className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}



{currentPopup?.id === "win10WithRulesPopup" && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-green-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-green-700 dark:text-green-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" strokeWidth="2" />
          <path d="M9 12l2 2 4-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Congratulations – Following Your Rules!
      </h2>
      <div className="mb-4 font-semibold text-gray-800 dark:text-gray-100 whitespace-pre-line">
       {(currentPopup.data?.message || "Amazing job! You achieved 10 wins in a row while sticking to your rules. This is the true mark of a disciplined trader.")
         .split('\n')
         .map((line, i) => (
           <React.Fragment key={i}>
             {formatThreeLossMessage(line)}
             <br />
           </React.Fragment>
         ))}
      </div>
      <button
        onClick={closeCurrentPopup}
        className="bg-green-500 hover:bg-green-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}



{currentPopup?.id === 'moodVolatilityPopup' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-blue-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-blue-700 dark:text-blue-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 20v-6m0 0V4m0 10c-2 0-4-1-4-4s2-4 4-4 4 1 4 4-2 4-4 4z" strokeWidth="2"/>
        </svg>
        Mood Volatility Warning
      </h2>
      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold text-blue-700 dark:text-blue-300">
          Your emotional state appears to be heightened.
        </span>
      </p>
      <p className="mb-6 font-semibold text-gray-700 dark:text-gray-300">
        Trading with intense emotions—whether it's excitement or stress—can lead to decisions based on feeling, not strategy. Your greatest asset is a calm and objective mind.
      </p>
      <p className="mb-6 font-semibold text-gray-700 dark:text-gray-300">
        Before your next trade, journal what you're feeling and why. We recommend you pause until your focus is back on your process.
      </p>
       <p className="font-bold mb-6 text-gray-700 dark:text-gray-300">
        If you consistently trade in a heightened emotional state, you may be using a position size, symbol, or timeframe that doesn't fit your personality.
      </p>
      <button
        onClick={closeCurrentPopup}
        className="bg-blue-500 hover:bg-blue-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}

{currentPopup?.id === 'twoLossAfterGoalWarning' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
        </svg>
        Warning – Losses After Goal
      </h2>
      <div className="font-bold mb-4 text-gray-800 dark:text-gray-100 whitespace-pre-line text-left overflow-visible">
          {(currentPopup.data?.message || "You had 2 losses after reaching your daily goal.")
            .split('\n')
            .map((line, i) => (
              <React.Fragment key={i}>
                {renderWithTiltTooltip(line)}
                <br />
              </React.Fragment>
          ))}
        </div>
      <button
        onClick={closeCurrentPopup}
        className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        OK
      </button>
    </div>
  </div>
)}




{currentPopup?.id === 'overRiskWarning' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >
        ×
      </button>

      <h2 className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
        </svg>
        Warning!
      </h2>

      <p className="mb-3 text-gray-800 dark:text-gray-100">
        You lost more than your planned risk amount.
      </p>

      <p className="mb-1">
        <span className="font-extrabold text-yellow-600 dark:text-yellow-400">
          Risk amount: {formatCurrency(currentPopup.data.riskAmount)}
        </span>
      </p>

      <p className="mb-4">
        <span className="font-extrabold text-yellow-600 dark:text-yellow-400">
          Amount lost: {formatCurrency(currentPopup.data.amountLost)}
        </span>
      </p>

      <p className="mb-4 font-semibold text-gray-700 dark:text-gray-300">
        Make sure that you are always using the proper stop loss and not moving it. If the trade is going too far against your analysis, you can always get out and get back in at the right time.
      </p>

      <p className="mb-2 font-extrabold text-yellow-600 dark:text-yellow-400">
        Keep your losers small and your winners big and never marry a trade idea!
      </p>

      <button
        onClick={closeCurrentPopup}
        className="mt-4 bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}


{currentPopup?.id === 'drawdownWarning' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-purple-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-purple-700 dark:text-purple-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 20v-6m0 0V4m0 10c-2 0-4-1-4-4s2-4 4-4 4 1 4 4-2 4-4 4z" strokeWidth="2"/>
        </svg>
        Drawdown Warning
      </h2>
      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold text-purple-700 dark:text-purple-300">
          Your account has experienced a drawdown of more than <span className="underline">{currentPopup.data.drawdownLimitPct}%</span> from its peak.
        </span>
      </p>
      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold">Drawdown amount: </span>
        <span className="font-bold text-purple-700 dark:text-purple-300">
          ${currentPopup.data.drawdownAmount?.toLocaleString(undefined, {maximumFractionDigits:2})}
        </span>
        <span className="ml-2 text-sm text-purple-500 dark:text-purple-300">
          ({currentPopup.data.drawdownPct?.toFixed(2)}%)
        </span>
      </p>
      <p className="mb-6 text-gray-700 dark:text-gray-300">
        Consider pausing to review your trades and protect your capital before proceeding.
      </p>
      <p className="font-bold mt-6 mb-6 text-gray-700 dark:text-gray-300">
        No one wins 100% of the time and there is nothing wrong with switching to demo until you are winning consistently and growing your account.
      </p>
      <button
        onClick={closeCurrentPopup}
        className="bg-purple-500 hover:bg-purple-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}


{currentPopup?.id === 'consecutiveWinsPopup' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-blue-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-blue-700 dark:text-blue-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" strokeWidth="2" />
          <path d="M8 12l2 2l4-4" strokeWidth="2" strokeLinecap="round" />
        </svg>
        Hot Streak!
      </h2>
      <p className="mb-3 text-gray-800 dark:text-gray-100">
        <span className="font-bold text-blue-700 dark:text-blue-300">You've won 5 trades in a row!</span>
      </p>
      <p className="text-gray-700 font-semibold dark:text-gray-300">
        Stay disciplined—don&apos;t increase your risk or change your plan just because you&apos;re on a hot streak.
      </p>
      <p className="mt-4 mb-6 font-semibold text-gray-700 dark:text-blue-300">
        Stick to your process and keep your edge!
      </p>
      <button
        onClick={closeCurrentPopup}
        className="bg-blue-500 hover:bg-blue-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}


{currentPopup?.id === 'accountGoalReached' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    {/* Confetti for celebration! */}
    <Confetti width={width} height={height} numberOfPieces={500} recycle={false} />
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-green-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-green-700 dark:text-green-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M5 13l4 4L19 7" strokeWidth="2" />
        </svg>
        Account Goal Achieved!
      </h2>
      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold text-green-700 dark:text-green-300">
          Congratulations! You've reached your account goal!
        </span>
      </p>
      <p className="mb-6 text-gray-700 dark:text-gray-300">
        Your balance is  <span className="font-bold">{formatCurrency(settings["Running Balance"])}</span><br />
        Your Goal was <span className="font-bold">{formatCurrency(settings["Account Goal"])}</span>
      </p>

      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold text-green-700 dark:text-green-300">
          Your hard work has paid off!
        </span>
      </p>
      <button
        onClick={closeCurrentPopup}
        className="bg-green-500 hover:bg-green-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}



{currentPopup?.id === 'lessonsLearnedStreakWarning' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-blue-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-blue-700 dark:text-blue-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" strokeWidth="2" />
          <path d="M12 6v6l4 2" strokeWidth="2" />
        </svg>
        Lessons Learned Streak!
      </h2>
      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold text-blue-700 dark:text-blue-300">Great job extracting value from your trades!</span>
      </p>
      <p className="mb-6 font-semibold text-gray-700 dark:text-gray-300">Consistently recording your lessons learned will accelerate your growth. <br/><br/> Review them every day during your pretrade routine until they become seared in your brain!</p>
      <button
        onClick={closeCurrentPopup}
        className="bg-blue-500 hover:bg-blue-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}

{currentPopup?.id === 'riskOfRuinWarning' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-red-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-red-700 dark:text-red-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 8v4l3 3" strokeWidth="2" strokeLinecap="round" />
          <circle cx="12" cy="12" r="10" strokeWidth="2" />
        </svg>
        Risk of Ruin Warning
      </h2>
      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold text-red-700 dark:text-red-300">
          Based on your current risk, your account could be wiped out in just <span className="underline">{lossProjection.tradeCount}</span> consecutive losses.
        </span>
      </p>
      <p className="mb-6 font-semibold text-gray-700 dark:text-gray-300">
        Consider lowering your risk per trade to protect your remaining capital.
      </p>
      <p className="mt-6 mb-6 font-semibold text-gray-700 dark:text-gray-300">
        Switch to your demo account and trade there until you are winning consistently. 
      </p>
      <p className="mt-6 mb-6 font-semibold text-blue-700 dark:text-blue-400">
        Don't be discouraged, you can do this!
      </p>
      <button
        onClick={closeCurrentPopup}
        className="bg-red-500 hover:bg-red-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}


{currentPopup?.id === 'twoLossWarning' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
        </svg>
        Warning - 2 Consecutive Losses!
      </h2>

      {/* MODIFICATION IS HERE */}
      <div className="mb-4 font-semibold text-gray-800 dark:text-gray-100 text-left">
        {TWO_LOSS_WARNING_TEXT.split('\n').map((line, i) => (
          <React.Fragment key={i}>
            {highlightHundredPercent(line)}
            <br />
          </React.Fragment>
        ))}
      </div>
      {/* END OF MODIFICATION */}

      <button
        onClick={closeCurrentPopup}
        className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}


{currentPopup?.id === 'threeLossWarning' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div
      className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-red-500 relative animate-fadeIn"
      role="dialog"
      aria-modal="true"
      aria-labelledby="three-loss-title"
      aria-describedby="three-loss-desc"
    >
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >
        ×
      </button>

      <h2
        id="three-loss-title"
        className="text-xl font-bold mb-4 text-red-700 dark:text-red-500 flex items-center"
      >
        <svg className="w-6 h-6 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" strokeWidth="2" />
          <path d="M12 8v4l3 3" strokeWidth="2" strokeLinecap="round" />
        </svg>
        Danger!
      </h2>

      <div id="three-loss-desc" className="mb-4 font-semibold text-gray-800 dark:text-gray-100">
        {currentPopup.data?.message.split('\n').map((line, i) => (
          <React.Fragment key={i}>
            {formatThreeLossMessage(line)}
            <br />
          </React.Fragment>
        ))}

        <div className="mt-4 text-blue-400 font-bold">
          {"You got this!!"}
        </div>
      </div>

      {/* Actions */}
      <div className="mt-6 flex flex-wrap gap-3 justify-end">
        {currentPopup.data?.videoLink && (
          <a
            href={currentPopup.data.videoLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border border-red-500 text-red-700 dark:text-red-300 font-bold px-4 py-2 rounded hover:bg-red-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 dark:focus:ring-offset-gray-900 transition"
          >
            {/* Play icon */}
            <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path d="M6 4l10 6-10 6V4z" />
            </svg>
            Watch: Dealing with Trading Losses
            <span className="sr-only">(opens in a new tab)</span>
          </a>
        )}

        <button
          onClick={closeCurrentPopup}
          className="bg-red-500 hover:bg-red-600 text-white font-bold px-6 py-2 rounded shadow transition"
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}


{showChecklist && (
  <PreTradeChecklistModal
    onClose={handleChecklistConfirm}
    onConfirm={handleChecklistConfirm}
  />
)}

      {/* Floating Calculator */}
      <button onClick={() => setShowCalculator(true)} className="fixed bottom-12 right-6 z-40 bg-gray-800 text-white p-4 rounded-full shadow-lg hover:scale-110 transition">
        <FaCalculator />
      </button>
      {showCalculator && <SimpleCalculator onClose={() => setShowCalculator(false)} />}

    </div>
  );
}